/**
 * The client/server wire protocol.
 *
 * The point of this file is that a change here fails to compile on *both* sides
 * at once, rather than failing silently at runtime (standards §1). Encoding and
 * decoding live here together for the same reason: a serialiser and its parser
 * that can drift apart eventually will.
 *
 * **Control messages are JSON. Chunk payloads are binary.**
 * A chunk is 32,768 blocks × 2 bytes = 64 KB. As JSON that would be roughly
 * 200 KB of comma-separated digits, parsed into a JavaScript array of boxed
 * numbers. As a binary frame it is 64 KB that becomes a `Uint16Array` with no
 * copy and no parsing.
 *
 * ⚠️ The server is authoritative and never trusts a client packet — not for
 * anti-cheat (everyone here is family, D1) but because a buggy client must not
 * be able to corrupt the shared persistent world (D3). Every decoder here
 * validates before it trusts.
 */

import { CHUNK_SIZE } from './world.ts';
import type { ChunkCoord } from './coords.ts';
import type { Weather } from './weather.ts';
import type { Stack } from './inventory.ts';
import { GIVE_MAX } from './give.ts';

/**
 * | Version | Change |
 * |---------|--------|
 * | 1 | Hello / welcome / chunk request, and binary chunk frames |
 * | 2 | `flat` on hello and welcome — the flat-terrain debug view (T49) |
 * | 3 | **Placing and breaking blocks (T52)** — `player_state` and `block_edit` in, `block_update` and `edit_rejected` back |
 * | 4 | **The server knows who you are (T52)** — `token` / `session` on hello, `player` and `gameSession` on welcome |
 * | 5 | **The world clock (T103)** — `clock` on welcome, and a `clock` message that resyncs it |
 * | 6 | **The weather (T103 piece ④)** — `weather` on welcome, and a `weather` message beside the clock |
 * | 7 | **The aurora's activity (T114)** — `kp` on the weather snapshot. ⚠️ **Global, not per-zone**, unlike everything else in that payload |
 * | 8 | **The server sends a KEY, not a sentence (T124)** — `edit_rejected.reason` became `reasonKey`, so the client decides what language the player reads it in |
 * | 9 | **What you are carrying (T149, D80)** — `inventory` on welcome and after every change, and `select_slot` going the other way |
 * | 10 | 🔴 **Rearranging the pack (T152)** — `move_item`, and it is the FIRST message that asks the server to change something rather than to tell us about it |
 * | 11 | 🗑️ **Throwing something away (T154)** — `discard_item`. ⚠️ **The first message with no undo**: what it destroys is gone |
 * | 12 | 🍖 **Food and health (T129, D64, D79)** — `vitals` down, `eat_item` and `use_block` up. 🔴 **The first message about the player's own BODY** rather than about the world or the pack |
 * | 13 | 🥶 **The cold (T131, D64, D85)** — `vitals` gains `shelter`, `exposure` and `bitingFactors`. 🔴 **The first thing on this wire whose answer depends on WHAT IS AROUND YOU** rather than on who you are or where you stand: two players a metre apart, one under a roof, get different numbers. ⚠️ **`reasonKey` gained values rather than a shape** |
 * | 14 | 🔨 **Crafting (T130 slice 3, D95)** — `craft` going up. 🔴 **The client names a RECIPE and a PLACE and nothing else**: not what it has, not what it spent, not what it got. A client that could name its own inputs could name four stones it does not own |
 * | 16 | 🔥 **A CONTAINER you can move things into and out of (T128, D96)** — `move_hearth_item` up, `hearth_contents` down. 🔴 **The first message that moves an item between two DIFFERENT tables**: `move_item` rearranges `player_inventory`, and a hearth slot is a row in `block_data`. ⚠️ **Each end NAMES its container** rather than relying on the number, because both start at 0 and a bare integer would mean two different boxes — the right item arriving in the wrong place, with nothing to show for it. ⚠️ An older client never asks and is never sent one |
 * | 15 | 🔥 **A block can be sent in a STATE (T108 slice 3, D47, D86)** — `block_update.block` may now be a **state key**, `eldstaedi[lit=true]`, rather than a plain block key. 🔴 **The first time this wire has carried anything but "which block"**, and it had to: a lit hearth has no block key of its own, so without it the server could not tell anybody a fire had lit. ⚠️ **An older client logs "unknown block — is this client stale?" and ignores the edit**, which is the right failure and was already written for a different reason |
 * | 17 | 🔦 **A block may be PLACED in a state (T128, D90)** — `block_edit.block` may now be a **state key**, `kyndill[facing=east]`, which protocol 15 already allowed in the other direction. 🔴 **The client PROPOSES the facing and the server VERIFIES it against the world** — it derives the block the torch would hang off and asks whether that face will hold one, exactly as `craft` re-reads the station from the world rather than trusting the client. ⚠️ **A modified client naming a different facing is harmless**, because every facing the server accepts is one the player could have produced by aiming somewhere else; what it cannot do is hang a torch on thin air |
 * | 18 | 🔨 **A tool that WEARS OUT (T130, D104)** — every `Stack` on this wire may now carry `used`, how many blocks that tool has already broken. 🔴 **The first PER-SLOT state the protocol has ever carried.** Everything before it described a slot completely as *which item* and *how many*; two identical axes are now genuinely different objects, which is exactly why a tool has `stack: 1`. ⚠️ **The shape did not change and the meaning did**, which is the dangerous kind: an older client reads a worn axe as a new one and would tell the player a lie about their own pack — so it is a version bump rather than an optional extra. ✅ **Absent still means none**, so every row written before 2026-09-01 reads correctly |
 * | 19 | 🪵 **A hearth slot says WHICH wood is in it (D96, D105)** — `hearth_contents` gains `woodItems`, one item key per wood slot. 🔴 **Added because the panel was LYING**: only counts were sent, so the client had to guess what a count was OF and guessed birch — while the fire was burning driftwood. 🎯 haugur found it in play: *"i placed 2 but there where not 2 on stock in the hearth."* ⚠️ **Optional on the wire**, so an older client ignores it and behaves exactly as it did. ⚠️ **This row was itself added a day late**, with protocol 20 — the bump shipped without it, which is the one thing this table exists to prevent |
 * | 20 | 📦 **A SECOND container, and the first whose slots hold ARBITRARY items (T135, D106)** — `move_chest_item` up, `chest_contents` down. 🔴 **Not `move_hearth_item` with a word changed.** A hearth slot holds a count of ONE known thing and can never swap; a chest slot holds a stack exactly as a pack slot does, so its move follows `moveStack`’s rules — including that half a stack cannot be swapped. ⚠️ **Chest-to-chest is allowed and pack-to-pack is not**: nothing else can tidy the inside of a chest, while `move_item` already owns the pack. ⚠️ **`chest_contents` carries full STACKS, not counts** — protocol 19 is the row above explaining why. ⚠️ An older client never asks and is never sent one |
 * | 21 | 💀 **DYING, AND WAKING UP SOMEWHERE (D21, D82, D108)** — `died` down, and nothing new going up. 🔴 **The first message that moves the player without the player asking.** Everything before it described the world, the pack or the body; this one says *you are somewhere else now*, and the client must obey it rather than treating it as a correction to reconcile — a client that argued would walk its own corpse back to where it died. ⚠️ **It carries `atHome`, which is not derivable from the coordinates**: waking at the beginning and waking at a hearth are different sentences on screen, and the client cannot tell which is which by looking at x, y and z. ⚠️ **The emptied pack is NOT in this message** — the ordinary `inventory` message carries that, because there must be one way a pack changes and not two. ⚠️ An older client never receives one, and on this server that means it simply never dies |
 * | 22 | 🏠 **A HEARTH SAYS WHETHER IT IS YOUR HOME (D108)** — `hearth_contents` gains `home`, true when this is the hearth you would wake at. 🔴 **Closes the one gap D108 named**: lighting a hearth moved your home and nothing on screen said so, so the rule was invisible until you died. ⚠️ **Not derivable on the client** — it is never told where its home is, only, on dying, that it woke there — so the server answers per hearth from `session.home`, with the same predicate `died` is built on. ⚠️ **Optional on the wire**, so an older client ignores it and draws the panel exactly as it did |
 * | 23 | 🏠 **THE SAME SCREEN FOR TWO CONTAINERS (D109)** — `chest_contents` gains `capacity`, 27 for a `kistill` and 54 for a `bur` door, and `move_chest_item` / `chest_contents` now serve the storehouse as well as the chest. 🔴 **The first message whose meaning depends on the BLOCK it names**: the server answers by the block's own rules (`containerRulesOf`) — the chest refuses food, the búr refuses everything else. ⚠️ **Optional on the wire**; an older client reads 27 and cannot open a búr anyway, because it has no such block |
 * | 24 | 🪧 **A CONTAINER SAYS WHAT IT IS CALLED (D110)** — `chest_contents` gains `title`, the `en.json` key for the container's own name. 🔴 **Added because the screen was working the name out from the SLOT COUNT** — `capacity > 27` meant *búr* — **and the skemma has 54 slots exactly as the búr does, so the store shed announced itself as the food store.** 🎯 haugur found it in play: *"the menu for skemma, it says Búr."* ⚠️ **The count was never the question**, and it would have been wrong again for the next 54-slot container without anybody touching the line. ✅ **The name now comes from `ContainerRules`, beside the rule that says what the container takes**, so what it holds and what it is called are answered in one place. ⚠️ **Optional on the wire**, and an older client falls back to the old count rule and is exactly as wrong as it was — which is the right failure, because the alternative is a blank title |
 * | 25 | 🔨 **THE CRAFT LIST CAN SEE YOUR CHESTS (T172, D111)** — `ask_nearby_materials` up, `nearby_materials` down. 🔴 **The screen was the only part of D111 that never shipped**: the bench has taken materials out of any container within 100 m since 2026-09-04, and the list went on greying rows by the pack alone — *you need 2 more stone* with the stone in a chest behind you. ⚠️ **The request carries NOTHING, on purpose.** The server answers from `session.at`, which is the position `craft` itself asks from, so **the screen and the authority ask the same question**; a client that could name its own position could stand beside somebody else's storehouse. 🔴 **The answer is for DISPLAY ONLY and the server recomputes everything on the craft**, so a stale list can cause a refusal and can never cause a wrong craft. ⚠️ **Slot numbers are not sent** — pooled stacks are safe for counting and for nothing else. ⚠️ An older client never asks and is never sent one |
 * | 26 | 🧍 **YOU CAN SEE EACH OTHER (T173)** — `players` down, and nothing new going up. 🔴 **The first message about somebody ELSE.** Everything before it described the world, the pack, the body or what is in reach; this one says *there is another person over there*. 🎯 haugur: *"we need to build a caracter so people can see each other in the game. if not people would only see block appearing but no one building them."* ⚠️ **The server has broadcast weather, the clock and block edits since the beginning and has NEVER broadcast a position** — a world where things were built by nobody. ⚠️ **Each client is sent everyone BUT itself**, so no client has to work out which one it is. ⚠️ **Sent on a beat rather than on every move**, and **only when the list has actually changed** — a room of people standing still costs nothing. ⚠️ **`y` is the FEET**, the same number `player_state` reports, so a body stands on it. ⚠️ **No name travelled yet** — that is protocol 27, the row below. ⚠️ **This row used to point at T174, which is the bug reporter and has nothing to do with names** |
 * | 27 | 🧍 **A NAME ABOVE THE HEAD (T176)** — `players[].name`, and nothing new going up. 🎯 **haugur, 2026-09-09:** *"i need to have players name above the head of their carachter."* 🔴 **Protocol 26 shipped bodies that were literally anonymous** — you could watch somebody build a house beside you with no way of knowing which of the family it was, and with three accounts on staging that stopped being a nicety. ⚠️ **It is the ACCOUNT's `display_name`, not the connection's id** — the id in the row above says in its own comment that it must never be shown, and it is a connection rather than an account, so two windows on one account would read as two strangers. 🔴 **NULLABLE, AND `null` IS THE ORDINARY CASE ON DEV** — development runs `ALLOW_ANONYMOUS_BUILD=true` with zero rows in `users`, so every body there genuinely has no name and **the client must draw nothing at all**: *Anonymous* over a head invents a person, and invents the SAME person on every head on screen. ⚠️ **Optional on the wire**, so an older client ignores it and draws the nameless bodies it always drew. ⬜ **A name does not hide behind a hill** — it is drawn in the HUD's overlay, which has no depth to test against, so it is hidden past **30 m** (🎯 his number) and when the head is off screen. **That is a stated limit, not an oversight** |
 * | 28 | 🔐 **THE WAY BACK IN (T88, D39)** — `signIn` on the welcome, and nothing new going up. 🔴 **You could be signed out and keep playing, and NOTHING SAID SO.** 🎯 **haugur, 2026-09-10:** *"if player has a browser open for long he automatically signs out but can keep playing ad not signed in."* **Three bug reports were filed that afternoon by a logged-out session** — the byline shipped that morning is what made it visible, because it said *an anonymous player on staging* while a real person was playing. ⚠️ **The credential is kept in `sessionStorage`, which is scoped to ONE TAB and thrown away when that tab closes** — so coming back from a bookmark starts as nobody, and **there was no path back.** ✅ **The server now sends WHERE to sign in**, and the client goes there and returns. 🔴 **PRESENT ONLY WHERE IDENTITY IS EXPECTED** — it is absent on development, which runs `ALLOW_ANONYMOUS_BUILD=true` and where being nobody is correct, **so one field carries both facts and they cannot disagree.** ⚠️ **The server sends a FINISHED URL and the client navigates to it** — the client never builds the destination, because a login page reached by a URL the caller composed is how an open redirect becomes a credential-harvesting page. ⚠️ **Optional on the wire**, so an older client ignores it and stays exactly as stranded as it was
 * | 29 | 🏠 **THE CLIENT IS TOLD WHERE HOME IS (T201)** — `home` down, and nothing new going up. 🎯 **haugur, 2026-09-14:** *"add Back to Home button on the map that returns you to your hearth."* 🔴 **Protocol 22 told a client whether THIS hearth is home; it never said WHERE home is.** That was enough for a line in the hearth panel and is not enough for a button on the map, and the difference is the whole reason for a new message rather than a wider old one. ⚠️ **It is a MESSAGE and not a field on the welcome, on purpose.** Home moves the moment you light a hearth, so a welcome-only field would be right at the start of the session and quietly wrong for the rest of it — *a second way in that forgets what the first one does*, which this repo has recorded seven times. ✅ **One helper sets `session.home` and sends this**, so the server cannot change it without saying so. ⚠️ **`null` is an ordinary answer, not a failure** — somebody who has never lit a hearth has no home, and on development, where `players` has no rows, it is `null` on every connect until a fire is lit. 🔴 **The client must draw that as a REASON, not as a dead button**: it greys the button and says to light a hearth first, the same choice the menu's Snow switch makes. ⚠️ **Optional on the wire**, so an older client ignores it and has no such button anyway.
 * | 30 | 🐑 **THERE ARE ANIMALS, AND THEY SAY WHAT THEY ARE DOING (T226, D117)** — `creatures` down, and nothing new going up. 🎯 **haugur, 2026-09-17:** *"yes animals should notice you, thats important"* — **and that one line is why this message exists at all.** 🔴 **The first message about something ALIVE that is not a person.** Protocol 26 broadcast bodies; this broadcasts minds. ⚠️ **Until now the only creature in this game was DERIVED** — a þorskur computed from its cell and the clock, sent to nobody, known to no server. **A creature that notices you cannot be derived, because the player's position is not in the formula.** 🎯 He described the result exactly: *"its a xerox copy of a picture of a cod floting in a sea that has no movement."* 🔴 **`doing` IS THE FIELD THAT MATTERS AND IT IS NOT DECORATION.** It carries the NAME of the behaviour currently in charge of moving — `Wander`, `Fleeing`, `Grazing` — **so an F8 bug report can say *"the sheep was `Fleeing` when it walked through the wall."*** ⚠️ **It is the entire justification for the brain being a flat list of named behaviours rather than a tree or a weighted blend**: for a project whose owner cannot read the code (D2), **a mind you cannot name is a mind you cannot fix.** ⚠️ **`y` is the FEET**, the same convention `players` uses, so a body stands on it rather than in it. ⚠️ **Sent on a beat and only when the list has CHANGED**, exactly as `players` is — a still herd costs nothing. ⚠️ **`id` is for telling one animal from another between beats and must never be shown**, the same rule as a player's connection id. ⚠️ **Optional on the wire**, so an older client ignores it and sees a world with no animals in it — which is the world it had yesterday.
 * | 37 | 🙌 **YOU CAN PICK A SHEEP UP, AND A SHEEP YOU CARRIED IS YOURS (T261, D123, D117)** — `carry` and `release` going UP, and `carrying` added to `players` coming down. 🎯 **haugur, 2026-09-23:** *"you can pick them up and put them in a pen if you find them in the wild."* 🔴 **THIS IS THE FIRST TIME ANY CREATURE IN THIS GAME HAS SURVIVED A RESTART.** Every animal until now was spawned near whoever was standing about and forgotten when they left — ⚠️ **which is correct for a fish, because nobody owns one**, and wrong for a sheep that was bought, penned and bred. 👉 ***A sheep nobody owns is a sheep nobody can lose, and losing them is the whole point of keeping them.*** 🔴 **CARRYING IS WHAT MAKES IT YOURS**, and that is `Minecraft/06_Mobs.md` §5's best idea: *instead of a curated list of protected species, the game infers value from PLAYER ACTION* — **it costs one boolean.** ⚠️ **And carrying is not a convenience, it is the ONLY way to move a sheep**: Minecraft uses a lead, a lead is string, **and the first string in this game is spun from the wool of the sheep you cannot yet move.** 🔴 **`carry` names a creature and that is a PROPOSAL, not a fact** — the server re-runs `carryTargetFrom` against its own copy of where every animal is, the same rule `spear` keeps. ⚠️ **`carrying` on `players` is the KIND, never the id**: everybody needs to draw a sheep in somebody's arms, and nobody needs to name which sheep. ⚠️ **A carried animal is ABSENT from `creatures`** — it is not standing anywhere, and a body drawn at its old spot would be a ghost. ⚠️ **Optional on the wire**, so an older client sees a world where nobody is carrying anything.
 * | 31 | 🔱 **YOU CAN TAKE A FISH WITH A SPEAR (T221, D115)** — `spear` going UP, and nothing new coming down. 🎯 **haugur chose the verb himself:** *"Thrust — jab at a fish within reach."* 🔴 **THE MESSAGE CARRIES ONE CREATURE ID AND NOTHING ELSE** — not what was caught, not what it is worth, not that it was in range. ⚠️ **That id is a PROPOSAL, not a fact.** The server runs the same `spearTargetFrom` the client aimed with, against its OWN copy of where every fish is, and refuses a name that does not survive it — because **a client that can name its own target can name a fish on the other side of the bay.** 👉 **It is the bug reporter's rule at a new subject:** *the browser never sends a NAME, it sends a credential.* ✅ **An id rather than a position, because a fish moves twenty times a second** and a position would be stale before it arrived; the id is already on the wire, one per animal, since protocol 30. ⚠️ **Nothing comes down** — the catch reaches everybody as the fish simply being absent from the next `creatures` beat, which is the message that already exists for exactly that.
 * | 32 | 🍲 **THE FIRE COOKS (T222, D121)** — `hearth_contents` grows from **six slots to eight**, and `woodItems` becomes **`slotItems`**, one item key for every slot rather than for the wood alone. 🎯 **haugur, 2026-09-22, giving the layout himself:** *"2 boxes on the loeft for what is to be coocked, 2 boxes below for the fuel and 4 boxes on the right for the finished cooked produgt."* 🔴 **The two new slots are numbered 6–7 although they are drawn FIRST.** A slot number is an identity on the wire and never a position on the glass — `hud-scene.ts` had already written that down about the original six (*"order is meaning… this must never be reordered"*), and renumbering would have put the right item in the wrong box for anybody who had not caught up. ⚠️ **`woodItems` covered only the wood slots and said why: *“charcoal is charcoal”*.** That was true when it was written and is false now — **the output shelf holds four cooked fish as well**, so a client deriving the item from the slot NUMBER would have drawn charcoal over somebody's dinner. 👉 ***The comment was never wrong; the world moved underneath it***, which is why the field is now named for all eight rather than for the group that happened to need it first. ⚠️ **Optional on the wire**, so a reader that has not caught up ignores it. 🔄 **And `ask_hearth_contents` goes UP**, sent on a two-second beat while the screen is open. 🔴 **The fire is the first container in this game that changes WHILE YOU ARE LOOKING AT IT.** A chest changes only when somebody moves something, and a hearth made one charcoal every FIVE REAL MINUTES — so nobody ever stood and watched one. **A fish cooks in about nineteen seconds**, so now they will, and a screen that sat still would read as a fire that does not work. ⚠️ **It NAMES the hearth, where protocol 25's ask carries nothing** — you can stand between two fires — **so the server checks both halves of the name**: that the block really is a hearth, and that it is within reach of where the SERVER believes the player is. ⚠️ **A refusal is silent**, because this is a poll rather than an action.
 * | 33 | 🥫🔥 **A CONTAINER MAY HAVE A FIRE (T222b, D121)** — `chest_contents` gains `fire`, and `move_hearth_item` may now name a **búr** as well as a hearth. 🎯 **haugur chose BOTH places:** *"same kinda o menu for the hearth also, should work the same."* 🔴 **The shape is the hearth's, field for field, because it IS a hearth** — the same `resolveHearth` runs it with `BUR_FIRE_RULES`. **The one difference is that it makes NO charcoal**, and that is **D71**: a búr refuses everything which is not food, so a búr filling with charcoal would be a locked rule broken from the inside by its own fire. ⚠️ **`move_hearth_item` was NOT copied into a `move_bur_fire_item`.** *Two functions that both half-answer one question is how they drift* — the server reads the block at the named position and picks the rules from it, exactly as `craft` derives its station rather than believing one. 🔴 **So a client cannot choose which rules apply to it**, which is the whole reason the server looks the block up. ⚠️ **No `home` field, on purpose**: a búr's fire is not somewhere you wake (D108) and does not warm you (D64 — 🎯 *"No — it only cooks"*), **and a field that is always false reads as a promise it might one day be true.** ⚠️ **Optional on the wire**, so a reader that has not caught up sees a búr with no fire — which is the búr it had yesterday.
 * | 34 | 🥫🔥 **A BÚR'S STORE AND ITS FIRE CAN REACH EACH OTHER (T222c, D121)** — `move_bur_item` going up. 🎯 **haugur, the hour the búr's fire shipped:** *"fire tab works fine but my fish are in the other tab, so if i want to cook them, i have to first drag them to onhand switch to fire and drag them there."* 🔴 **Cooking food out of your own food store took a tab switch and three drags.** ⚠️ **It is its own message because it is the pair nobody had:** `move_item` is pack-to-pack, `move_chest_item` pack-to-store, `move_hearth_item` pack-to-fire — **every one of them has the pack at an end**, and this is the only move on that screen with the pack at NEITHER. **Widening one of the others would make a message that half-answers two questions**, which is T247's fault wearing a new coat. ✅ **Cheap, because a búr's food and its fire are ONE `block_data` row** — one read, one write, and no way to write one half without the other. 🔴 **Both ends keep their own rules and neither is relaxed:** the fire's input takes only what has a `cooksTo`, and the store takes only food — **D71** — so a log cannot reach the food house by going through the fire. ⚠️ **No swap**, for the reason T248 is open about. ⚠️ An older client never sends one.
 * | 38 | 🔪 **YOU CAN KILL A SHEEP (D125, D123)** — `strike` going UP, and nothing new coming down. 🎯 **haugur chose every verb:** a knife, several strikes with the sheep RUNNING between them, and on the last it falls over and the meat and the gæra go into your pack. 🔴 **One creature id and nothing else, and that id is a PROPOSAL** — the server re-runs `knifeTargetFrom` from where IT believes your eye is, exactly as `spear` and `carry` do. ⚠️ **Nothing comes down**: the running is the sheep's `doing` turning to `Panic` on the next `creatures` beat, the fall is `doing` turning to `Dead`, and the meat arrives on the ordinary `inventory` message — **three messages that already exist.** ⚠️ **A refusal is `edit_rejected`** — somebody else's sheep, no knife, out of reach — the same answer a missed spear gets.
 * | 39 | 🩸 **YOU CAN SEE A HIT LAND** — `creatures[].hits`, and nothing new going up. 🎯 **haugur, 2026-09-28:** *"there need to be somekind of indication that im hurting it, like it flashing red when i hit it."* 🔴 **A COUNT, NEVER A FLAG** — a flag on for one 100 ms beat can fall between two beats a client never sees; a count that went up cannot be missed. ⚠️ **It never goes down**: healing resets the strikes toward a kill, not this. ✅ **Everybody near sees the flash**, not only the one who struck — the server says it, the client draws it. ⚠️ **Optional on the wire**, so an older client ignores it and sees no flash.
 * | 40 | ✂️ **YOU CAN SHEAR A SHEEP (D126)** — `shear` going UP, and `creatures[].shorn` coming down. 🎯 **haugur chose every rule:** the knife and the RIGHT button, any month; **by hand in May and June**; **2 `ull`**; the wool back after **one real hour and one graze**. 🔴 **One creature id, and that id is a PROPOSAL** — the server re-runs `knifeTargetFrom`, reads your hand and the GAME's month, and refuses somebody else's sheep, exactly as `strike` does. ⚠️ **A refusal is `edit_rejected`**, and an empty hand in winter is told **why** (`edit.rejected.notLoose`) — that sentence is how a player learns the season. ✅ **`shorn` is optional**, so an older client draws every sheep woolly.
 * | 41 | 🧵 **THE VEFSTOFA SPINS (D127)** — `chest_contents.spin`, and nothing new going up. 🎯 **haugur named a fourth house** — *"Vefstofa, spinning only first"*. It is a two-slot container (wool in, thread out) and **every existing message already moves things in and out of it**; what is new is ONE word saying whether it is `spinning`, `idle` or `full`, so the screen can say so and keep asking while it spins. ⚠️ **Optional**, so an older client shows the two slots and no sentence.
 * | 42 | 🪢 **THE LEAD AND THE TETHER PEG (D128)** — `lead` and `unlead` going UP; `creatures[].ledBy` and `creatures[].tether` coming down; and `welcome.you`, this connection's own id. 🎯 **haugur chose every rule:** 3 þráður make a lead, a led sheep follows within 3 m and snaps past 12, several at once, a peg ties them within 5 m. 🔴 **One creature id, a PROPOSAL** — the server re-aims, reads the hand and refuses somebody else's lead, as `carry` does. ⚠️ **`ledBy` is a connection id**, matched against `players[].id` to find whose hand the rope hangs from — and against `welcome.you` for your own, because `players` never includes the viewer. ⚠️ **Tying and untying are `use_block` on the peg**: nothing new for them. ✅ **All three fields are optional**, so an older client draws no rope.
 * | 43 | 🎣 **THE FISHING ROD (D129)** — `cast` and `reel` going UP, no fields: the server aims from its own idea of your eye, as every verb does. `floats` coming DOWN: every float in the water — who cast it, where, and whether it is under — to everybody including the caster, because `players` never includes the viewer. 🎯 **haugur chose a REAL fish**: the one that swims to your float is the one you land, and it swims there on the ordinary `creatures` beat, doing `Lured`. ✅ **Everything new is its own message**, so an older client sees no float.
 * | 46 | ❤️ **A HEART OVER A SHEEP THAT HAS MATED (D130)** — `creatures[].mated`, true while a sheep is carrying or has mated this season: the sheep that will NOT take the turf. 🎯 **haugur:** *"a little red heart floating above the head of a sheep that is already carrying a lamb or a sheep that has already mated."* ✅ **Optional**, so an older client draws no heart.
 * | 47 | 🎁 **AN ADMIN CAN GIVE THEMSELF AN ITEM** — `give` going UP (an item key and a count), and `welcome.player.admin` coming down so the client knows whether to offer the box. 🎯 **haugur, 2026-10-01**, having starved mid-test and lost his kit: *"which is annoying when one is test alot"* — and he chose a give command. 🔴 **The server checks `users.is_admin` itself**; the client's flag only decides whether `T` opens anything. ✅ **Optional**, so an older client never offers it
 * | 45 | 💙 **A FED SHEEP SHOWS IT (D130)** — `creatures[].ready`, true for the thirty seconds a sheep is ready to mate. 🎯 **haugur:** *"we need somekind of visual indicator that placing the grass on the sheep worked … make it flash blue once"* — then *"flash and pulse"*: blue once when it eats, then a blue pulse every few seconds while it stays ready. ✅ **Optional**, so an older client shows nothing.
 * | 44 | 🐑🐣 **BREEDING AND THE LAMB (D130)** — `feed` going UP, one creature id; `creatures[].lamb` coming down, how big a lamb is as a fraction of an adult. 🎯 **haugur chose every rule:** two of your own sheep in a built pen, one `gras` each in December, 143 game days, a lamb that follows its mother and is grown at réttir. 🔴 **One creature id, a PROPOSAL** — the server re-aims, reads the hand, the pen, the owner and the GAME's month, exactly as `shear` does. ⚠️ **A refusal is `edit_rejected`**, and so is the good news (*"the sheep is ready"*, *"a lamb was born"*) — the same line `game.notice.leadSnapped` already uses. ✅ **`lamb` is optional**, so an older client draws a lamb as a full-size sheep.
 * | 50 | 🛏️ **THE RÚM — SLEEPING (D133)** — `wake` going UP, no fields: lying down is a right-click on a bed, the `use_block` that already exists. `asleep` coming DOWN to the sleeper alone: which bed, or `null` and where you stand up. `players[].lying` coming down: draw this body lying in a bed — **and a player who logged out asleep is IN the list**, so family can see you sleeping. 🎯 haugur: *"when i lay in my bed and sleep my caracter and all animals are imune to time"*. ✅ **`lying` is optional**, so an older client draws a sleeper standing.
 * | 49 | 🐣🙌 **A CARRIED LAMB IS A LAMB** — `carrying.lamb` and `players[].carryingLamb` coming down: how big the lamb in somebody's arms is, as a fraction of an adult, absent for a grown sheep. 🎯 **haugur, 2026-10-02:** *"yes fix the carried lamb too"* — a lamb picked up was drawn full size and named *a sheep*, because carrying named only the KIND. ✅ **Optional**, so an older client draws an adult as before.
 * | 48 | 🥣🐑 **THE JATA AND SHEEP HUNGER (T286)** — three things coming DOWN, nothing new going up (filling a jata is `use_block` with hay in hand). ① `edit_rejected.reasonValues`, so a notice can carry numbers — *"the jata holds 142 of 300"*; the comment on that message said it would be a protocol bump the day it was wanted. ② `creatures[].hunger`, `hungry` or `starving` for a KEPT sheep (absent when fed, and always absent for a wild one), so looking at your sheep says how it is. ③ `flock_warning`, to ONE player: where their starving sheep are and how many game days the worst has left — the arrow at the screen edge and the warning at login. 🎯 **haugur:** *"a short note in hud will not cut it."* ✅ **All optional or new**, so an older client simply shows none of it.
 * | 51 | 🦊 **THE ARCTIC FOX (decision 134)** — `creatures[].prey`, true while a fox has a lamb in its jaws, so every client draws the lamb in its mouth. Nothing new goes UP: a fox is struck with the knife's existing `strike`. The fox itself rides the ordinary `creatures` beat as kind `refur`, doing `Sleep`, `Watch`, `Flee`, `Stalk`, `Carry`, `Pounce` or `Sniff`. ✅ **Optional**, so an older client draws no lamb — and draws no fox at all, because it does not know the kind.
 * | 52 | 🏠 **THE VEFSTOFA BECOMES A PIT HOUSE YOU WALK INTO (decision 135)** — five new blocks (`vefstofudyr`, `vefstofuhluti`, `vefstofuthrep`, `vefstofuofn`, `vefstofubekkur`, ids 57–61) and their states (the door: facing × open × half; the oven: lit). Nothing new on the wire: the door opens with `use_block`, the bench is a container, the oven is a fire. ⚠️ **A bump because an older client does not know the ids** and would draw the house as unknown cubes.
 * | 53 | 🧵 **THE VEFSTAÐUR — THE UPRIGHT LOOM (decision 136)** — three new blocks (`vefstadur` and its 27 states, facing × weave; `vefstadurhluti`; `kljasteinn`, ids 63–65) and `chest_contents.loom`, what the loom is doing and how far. Nothing new going up: the loom is a three-slot container moved with the existing messages, and a kljásteinn is picked with `use_block`. ⚠️ **A bump because an older client does not know the ids.**
 */
export const PROTOCOL_VERSION = 53;

/** Blocks in one chunk. */
export const BLOCKS_PER_CHUNK = CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE;

// ── JSON control messages ───────────────────────────────────────────────────

export interface HelloMessage {
  readonly type: 'hello';
  readonly protocolVersion: number;
  /**
   * Ask for the flat-terrain debug view (T49) — every column at one height.
   *
   * A property of the *connection*, not of the world: one client can be flat
   * while another walks the real terrain, and neither affects what is
   * generated for anyone else. Absent means normal terrain.
   */
  readonly flat?: boolean;
  /**
   * A **play token** from the website's Play button (T52).
   *
   * ⚠️ Single use and two minutes old at most, because it arrives in the URL —
   * where it can end up in a browser history, a chat message or a screenshot.
   * The server spends it and answers with a `gameSession`, which is what gets
   * kept. See `migrations/005_play_tokens.sql`.
   */
  readonly token?: string;
  /**
   * A **game session** from a previous exchange, for a reconnect.
   *
   * ⚠️ This is why the two are separate credentials. The flat-view switch
   * (T49) closes the socket and opens a new one, and so does a page reload — so
   * whatever identifies you has to survive a reconnect, and a URL token that
   * survived would be a shareable account grant for as long as it lived.
   */
  readonly session?: string;
}

export interface WelcomeMessage {
  readonly type: 'welcome';
  readonly protocolVersion: number;
  readonly worldgenVersion: number;
  readonly serverTime: string;
  /** Where a new player starts, in ISN93 metres. */
  readonly spawn: { readonly x: number; readonly y: number; readonly z: number };
  /**
   * Whether the server is actually sending flat terrain.
   *
   * ⚠️ Echoed back deliberately, and it is what the HUD must display — never
   * the client's own request. A debug view mistaken for real terrain wastes a
   * day, and so does a flag that failed to arrive but is shown as if it had.
   */
  readonly flat: boolean;
  /**
   * Who the server thinks you are, or `null` if nobody (T52).
   *
   * ⚠️ **`null` is a normal answer, not an error.** An unidentified connection
   * may walk and fly and look at everything; it may not change a block, because
   * every change is attributed to somebody. That keeps the world open to a
   * quick look without an account, which is how development works today.
   */
  readonly player: {
    readonly displayName: string;
    /** 🎁 Protocol 47 — present and `true` only for an admin, who may `give`. */
    readonly admin?: true;
  } | null;
  /**
   * ⚠️ Present **only** on the reply to a hello that spent a play token — this
   * is the credential itself, and re-sending it on every welcome would put it
   * on the wire far more often than it needs to be. The client stores it and
   * uses it for reconnects.
   */
  readonly gameSession?: string;
  /**
   * 🪢 **This connection's own id** — protocol 42, D128. The same id other
   * players see as `players[].id`; a client compares `creatures[].ledBy` with
   * it to know which ropes hang from its OWN hand. ⚠️ **Not a credential** — it
   * is broadcast to everybody near you already.
   */
  readonly you?: string;
  /**
   * 🔐 **Where to go and sign in, when this server expects you to be somebody**
   * (T88, D39).
   *
   * 🔴 **Present ONLY where identity is expected.** Development runs
   * `ALLOW_ANONYMOUS_BUILD=true` and being nobody there is correct, so the
   * field is simply absent and the client does nothing. **One field carries
   * both "you should be signed in" and "here is where" — they cannot disagree.**
   *
   * 🔴 **A FINISHED URL, built by the server.** The client navigates to it and
   * never composes it. A login page reached by a destination the caller put
   * together is how an open redirect turns a sign-in into a credential-
   * harvesting page, which T88 warns about in as many words.
   *
   * ⚠️ **Sent on every welcome, not only the first**, because it is not a
   * credential — it is a public address, and the client needs it precisely on
   * the welcomes that carry no player.
   */
  readonly signIn?: string;
  /**
   * What time it is in the world (T103).
   *
   * ⚠️ **On the welcome as well as in its own message**, so a client knows the
   * date before the first resync arrives. A HUD that shows nothing for ten
   * seconds after joining looks exactly like a clock that is not running.
   */
  readonly clock: WorldClockSnapshot;
  /**
   * What the weather is doing where you are (T103 piece ④).
   *
   * ⚠️ **On the welcome for the same reason the clock is** — the sky is drawn on
   * the first frame, and a client that had to wait for the first resync would
   * open on the default overcast grey and then change colour half a minute
   * later, in front of the player, for no visible reason.
   *
   * ⚠️ **`null` means the server has not managed to draw a year** (D57) — a
   * database problem, not a calm day. The client keeps the measured overcast
   * default rather than inventing sunshine.
   */
  readonly weather: WeatherSnapshot | null;
}

/**
 * The world clock, on the wire (T103).
 *
 * ⚠️ **One number, not a formatted date.** Both sides derive the date from it
 * with the same function in `clock.ts`, so they cannot disagree about what day
 * it is — and the client can carry the clock forward between resyncs instead of
 * stepping once every ten seconds.
 */
export interface WorldClockSnapshot {
  /**
   * Accumulated **played** real milliseconds since the world began.
   *
   * ⚠️ Not wall-clock time since the world was created. The clock advances only
   * while somebody is connected (D41), and on a family server those two numbers
   * are days apart within a week.
   */
  readonly playedMs: number;
}

/**
 * The world clock, resent every so often (T103).
 *
 * The client runs the clock forward locally between these, because a date that
 * jumps twelve game minutes at a time reads as a stutter rather than as a
 * clock. This exists to stop that local run drifting, and to be the one place
 * the server's answer wins.
 */
export interface WorldClockMessage {
  readonly type: 'clock';
  readonly clock: WorldClockSnapshot;
}

/**
 * The weather where one player is standing, on the wire (T103 piece ④).
 *
 * 🔴 **The server computes this per player and sends it; the client never reads
 * the tables.** The alternative was shipping the archive to the browser, and the
 * archive is **11 MB** of CSV — five zones × thirty years. It would also put the
 * zone lookup, the blend and the year draw on both sides of the wire, where they
 * could disagree about what the weather is. **One side owns it.**
 *
 * ⚠️ **Per player, not per world.** D58 makes Iceland five zones, so two players
 * far apart genuinely have different weather. This is the first message in the
 * protocol whose *content* depends on where the recipient is standing.
 */
export interface WeatherSnapshot {
  /**
   * The Icelandic name of the zone whose weather this mostly is.
   *
   * For the HUD and for diagnosis. ⚠️ **Near a boundary the reading is a blend
   * of two zones** (D58) and this names only the heavier one — so it says where
   * the weather came from, not that it came from one place.
   */
  readonly zone: string;
  /**
   * Which real archived year the world is playing (D57).
   *
   * ⚠️ Not shown to the player as part of the game — the calendar deliberately
   * has no year (D41) — but it is the single most useful number when the weather
   * looks wrong, because it says which thirty-year row to go and check.
   */
  readonly realYear: number;
  /**
   * The reading itself, or `null` if the server has no table for this moment.
   *
   * ⚠️ **`null` is a real answer and the client must handle it** rather than
   * substituting calm and mild. A missing table is a broken data build, and a
   * pleasant day is the one symptom nobody would ever report.
   */
  readonly weather: Weather | null;
  /**
   * 🌌 **The planetary geomagnetic index, 0–9** — the aurora's third ingredient
   * (T114, **D61**), from the same drawn year and the same moment.
   *
   * 🔴 **It is GLOBAL, not per-zone**, unlike everything else in this snapshot.
   * It travels here because it arrives on the same beat and is read from the same
   * hour; it is not a property of where you are standing.
   *
   * ⚠️ **`null` means the table is missing, and the client draws no aurora.** It
   * does not mean a quiet sky — **Kp 0 is a real value with a real faint
   * display**, so substituting it would turn a broken data build into a mildly
   * disappointing night nobody would ever investigate.
   */
  readonly kp: number | null;
}

/**
 * The weather, resent on the same beat as the clock (T103 piece ④).
 *
 * **Every 30 real seconds**, which is **36 game minutes** — and the tables step
 * every three game hours, so the sky can only change every five of these. The
 * client eases between them; see `packages/client/src/clientweather.ts`.
 */
export interface WorldWeatherMessage {
  readonly type: 'weather';
  readonly weather: WeatherSnapshot;
}

/** Ask for chunks. The server answers with one binary frame each. */
export interface ChunkRequestMessage {
  readonly type: 'chunk_request';
  readonly chunks: readonly ChunkCoord[];
}

/** Sent instead of a binary frame when generation failed. */
export interface ChunkErrorMessage {
  readonly type: 'chunk_error';
  readonly chunk: ChunkCoord;
  readonly reason: string;
}

/**
 * Where the player is, sent a few times a second (T52).
 *
 * ⚠️ **The server had no idea where anyone was until this existed**, and that
 * is not a cosmetic gap: reach is the rule that stops a modified client editing
 * the far side of the island, and reach cannot be checked without a position to
 * measure from. So this message is a prerequisite for `block_edit`, not a
 * convenience.
 *
 * It is also what `players` is written from — position, facing and flight are
 * exactly the columns in the table.
 */
export interface PlayerStateMessage {
  readonly type: 'player_state';
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly pitch: number;
  readonly flying: boolean;
}

/**
 * Place or break one block (T52).
 *
 * ⚠️ **`block` is a KEY, never a number.** The numeric id is a representation
 * pinned in `palette-lock.json` (T80); the key is the identity. On the wire that
 * costs a few bytes at the rate a person can click, and it buys two things: a
 * log line that says `birki` instead of `13`, and a client running a different
 * palette build that fails loudly rather than placing the wrong block.
 *
 * Breaking is `block: 'air'`. There is no separate break message, because a
 * break is exactly an edit to air and two messages would be two code paths that
 * must agree about validation forever.
 */
export interface BlockEditMessage {
  readonly type: 'block_edit';
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /**
   * The block to put there, as a key.
   *
   * 🆕 **Protocol 17: this may be a STATE key** — `kyndill[facing=east]` —
   * and not only a plain block key. `block_update` has been allowed to carry
   * one since protocol 15; this is the same permission in the other direction.
   *
   * 🔴 **It is a CLAIM, not an instruction.** The server already refuses a
   * key that does not match the item in your hand; for a state it also checks
   * the state is one the world would actually allow at that position — a torch
   * needs something to hang off. **The client proposes and the server
   * verifies**, which is `craft`'s rule applied to a second message.
   */
  readonly block: string;
}

/** One block that has actually changed, as the server sees it. */
export interface BlockChange {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly block: string;
}

/**
 * Blocks that have changed — the server's answer, and the only thing that moves
 * a block in anybody's world.
 *
 * ⚠️ **The client does not predict its own edits.** It sends `block_edit` and
 * waits for this to come back before changing anything on screen. Prediction
 * would mean a rollback path for every rejection, and a rollback path that is
 * wrong desynchronises one player's world from everyone else's in a way that
 * looks like a worldgen bug. The world is on a LAN and this is a shared
 * persistent world (D3); correctness is worth more here than the few
 * milliseconds prediction would save.
 *
 * Sent to every connected player, not just the one who made the edit — that is
 * what makes it a shared world rather than several private ones.
 */
export interface BlockUpdateMessage {
  readonly type: 'block_update';
  readonly edits: readonly BlockChange[];
}

/**
 * An edit the server refused, and why.
 *
 * ⚠️ Refusals are **told to the player**, not just logged. An edit that
 * silently does nothing is indistinguishable from a broken game, and the person
 * who has to diagnose it cannot read the server logs (D2).
 *
 * 🔴 **A KEY, not a sentence (protocol 8, T124).** If the server sent finished
 * English, the server would have to know what language every player reads — and
 * it would have to be told, and remember, and be redeployed to fix a typo in a
 * sentence. Sending `edit.rejected.notSignedIn` means the *client* decides, out
 * of the language file it already has.
 *
 * ⚠️ **Typed `string` here on purpose, and `MessageKey` at the server end.** The
 * server cannot mistype a key without failing its build; the client treats what
 * arrives as text from outside and shows the key itself if it does not know it.
 *
 * ⚠️ **No values travel with the key**, deliberately — so a refusal cannot say
 * *"you need 3 more wood"* yet. Every refusal we have is a whole sentence on its
 * own, and adding a `reasonValues` field before anything needs one is machinery
 * built on a guess (newSession §7 rule 7). It is a protocol bump when it is
 * wanted, which is a thing this project does routinely.
 */
export interface EditRejectedMessage {
  readonly type: 'edit_rejected';
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly reasonKey: string;
  /**
   * 🥣 **Numbers for the sentence's placeholders — protocol 48, T286.** The day
   * the comment above said would come: *"the jata holds 142 of 300"*. ⚠️ Still
   * a key and VALUES, never a finished sentence — the client fills them in, in
   * its own language. Optional, so every existing refusal is unchanged.
   */
  readonly reasonValues?: Readonly<Record<string, string | number>>;
}

/**
 * 🎒 What the player is carrying — the whole inventory, every time.
 *
 * 🔴 **Sent WHOLE rather than as a delta, and that is a deliberate trade.**
 * The database stores one row per occupied slot precisely so a change writes one
 * row (migration `013`); the wire does the opposite, because the two are solving
 * different problems. A delta needs the client to hold state the server can
 * disagree with, and the failure mode of a missed delta is **a player who thinks
 * they are carrying something they are not** — which they discover only when a
 * break is refused for no visible reason. A full pack is 34 slots of small
 * objects, a few hundred bytes, sent when something changes and never per frame.
 *
 * ⚠️ **Only ever sent to the player it belongs to.** Unlike `block_update`,
 * which goes to everyone because the world is shared, this is one player's own
 * business.
 */
export interface InventoryMessage {
  readonly type: 'inventory';
  /** Occupied slots only. **An empty slot is an absent entry, never a zero.** */
  readonly stacks: readonly Stack[];
  /** Which of slots 0–9 is in hand. */
  readonly selected: number;
}

/**
 * Choose which on-hand slot is in hand.
 *
 * The number keys, and the pattern `Development/Minecraft/09` marks as
 * load-bearing: *"the hotbar as a quick-select"* — load-bearing as a pattern,
 * flavour in the count. Ours is ten because haugur chose ten.
 *
 * ⚠️ **The server decides whether the slot is legal**, as with every other
 * message here. A client asking for slot 40 is told nothing and changes nothing.
 */
export interface SelectSlotMessage {
  readonly type: 'select_slot';
  readonly slot: number;
}

/**
 * 🔴 **Move some of what is in one slot into another.** The first message in
 * this protocol that asks the server to **rearrange** something.
 *
 * Every message before it was one of two shapes: *here is where I am* or *here
 * is what I did to a block*. The inventory itself has only ever travelled one
 * way — the server telling the player what they hold. This one goes the other
 * way and changes state that persists.
 *
 * ## ⚠️ There is no *held* item, deliberately
 *
 * Minecraft carries a stack on the cursor, which means an item can be in no
 * slot at all — a third place for things to live, which has to survive a
 * disconnection. **Ours never leaves its slot until it arrives in another
 * one:** the picking-up is a drawing the client does, and only the finished
 * move is sent. A connection that drops mid-drag therefore loses nothing,
 * because as far as the server is concerned nothing had begun.
 *
 * 🔴 **The trade is that the client can be out of date**, and the answer to
 * that is {@link InventoryMessage}: a move the server does not recognise —
 * more items than are there, an empty source, half a stack onto a different
 * item — changes nothing, and the whole inventory is sent back so the screen
 * stops disagreeing. ⚠️ **Nothing is ever clamped to fit.** A silently
 * adjusted move is how a player ends up holding a number nobody chose.
 *
 * `from` and `to` are slots **0–33**, which is one numbering across the ten on
 * hand and the twenty-four in the pack — so moving something out of the pack
 * and into your hand is this same message with no special case.
 */
export interface MoveItemMessage {
  readonly type: 'move_item';
  /** The slot the items are leaving, 0–33. */
  readonly from: number;
  /** The slot they are going to, 0–33. */
  readonly to: number;
  /** How many. Fewer than the whole stack is how a stack is **split**. */
  readonly count: number;
}

/**
 * 🗑️ **Throw some of a slot away.** It is destroyed.
 *
 * 🎯 **haugur, 2026-08-19**, choosing between three ways to get rid of
 * something: *"ok 1 is fine for now"* — destroyed, rather than dropped on the
 * ground or built back into the world.
 *
 * 🔴 **Why this is not `move_item` to a magic slot.** A destination that means
 * "nowhere" would make every reader of `move_item` check for it, and a
 * mistyped slot number would silently destroy something instead of failing.
 * **An irreversible action gets its own name.**
 *
 * ⚠️ **There is no undo, and D21 already takes everything you carry when you
 * die** — so the protection is in the interface rather than here: throwing
 * something away takes **two deliberate actions** and the game **says what it
 * threw away**. This layer only guarantees the shape.
 */
export interface DiscardItemMessage {
  readonly type: 'discard_item';
  /** The slot to take from, 0–37. */
  readonly from: number;
  /** How many. Fewer than the whole stack leaves the rest where it is. */
  readonly count: number;
}

/**
 * 🍖 **How the player's own body is doing** — health, food, and why (protocol 12).
 *
 * 🔴 **The first message in this protocol about the PLAYER rather than about
 * the world or the pack.** Everything before it was *what time is it*, *what is
 * the weather doing*, *what are you carrying*.
 *
 * ## ⚠️ A correction, not the thing that moves the bar
 *
 * **Exactly the shape `clock` already uses**, and for the same reason. This
 * arrives on the server's save interval — every 30 seconds — and a bar that
 * only moved on that beat would visibly jump. So the client runs `stepVitals`
 * from `@lofi/shared` every frame and this message **puts it right**. Both ends
 * call the same function, so the correction is normally a no-op of a fraction
 * of a percent; if it ever is not, that is a real disagreement worth seeing
 * rather than two implementations drifting.
 *
 * ⚠️ **Only ever sent to the player it belongs to**, like `inventory` and
 * unlike `block_update`. How hungry somebody else is is their own business.
 */
export interface VitalsMessage {
  readonly type: 'vitals';
  /** 0–100. */
  readonly healthPercent: number;
  /** 0–100. */
  readonly foodPercent: number;
  /**
   * Why, as a **key** — `hungry`, `starving`, `recovering` — or `null` for
   * nothing worth saying.
   *
   * 🔴 **D64 §7 requires the HUD to name the reason in words**, and §18 of the
   * gameplay notes says why it earns its place here: with one bar for health
   * you cannot tell starving from injured. ⚠️ **A key, not a sentence** —
   * protocol 8's rule, unchanged: the server does not know what language this
   * player reads.
   */
  readonly reasonKey: string | null;
  /**
   * 🥶 **What the weather is costing you** (protocol 13, T131).
   *
   * 🔴 **The multiplier on the food drain, and the whole of what the cold
   * does.** 1 is comfortable. It never slows you, never blocks you and never
   * takes the controls (**D24**) — ADR-0016 §2: exposure is *a condition, like
   * standing in fire*.
   *
   * ⚠️ **Sent even though the client can work it out**, and that is deliberate:
   * the client's own chunks answer AIR for anything still streaming in, so its
   * shelter answer can be briefly wrong. **The server owns the world and the
   * database, so the server owns this** — the same arrangement `clock` and
   * `vitals` already have, where the client runs the shared function between
   * corrections and this puts it right.
   */
  readonly exposure: number;
  /**
   * 🏠 Which rung of D64 §3's shelter ladder the player is on (T132).
   *
   * `open` · `lee` · `enclosed` · `hearth`. ✅ **`hearth` started arriving on
   * 2026-08-31**, and the bet this field was put on the wire for paid off: it
   * needed **no protocol change** — 16 is still 16.
   */
  readonly shelter: string;
  /**
   * 🌡️ Which of cold, wind and wet are actually costing something, worst first.
   *
   * 🔴 **D64 §7: the HUD says WHY, in words.** ⚠️ **Only factors that COST
   * something appear**, so a factor tuned to zero is silent — which is why the
   * screen cannot blame the rain before rain has a price (haugur, 2026-08-21).
   * ⚠️ **Keys, never sentences** — protocol 8's rule.
   */
  readonly bitingFactors: readonly string[];
}

/**
 * 🍇 **Eat one of what is in a slot** (protocol 12).
 *
 * ⚠️ **A slot number, not an item key.** The key would let a client ask to eat
 * something it is not carrying, and the server would then have to search the
 * pack for it and choose which stack — two decisions it does not need to make.
 * The slot is unambiguous, and a slot holding something inedible is simply
 * refused.
 *
 * **One at a time, deliberately.** There is no `count`: eating five berries is
 * five messages, which is five separate looks at a bar that is filling up. A
 * count would let a client swallow a whole stack into a bar that stopped
 * accepting food after the first one.
 */
export interface EatItemMessage {
  readonly type: 'eat_item';
  /** The slot to eat from, 0–33. */
  readonly slot: number;
}

/**
 * 🫐 **Use the block you are pointing at, without changing it** (protocol 12).
 *
 * Today that means one thing: **picking berries off the lyng.** The heath stays
 * where it is and a krækiber or a bláber arrives in the pack.
 *
 * 🔴 **Why this is not a `block_edit`.** Every message before it that touched
 * the world *replaced a block*, and `block_update` going back out is what makes
 * the change appear on everybody's screen. This changes **no block at all**, so
 * there is nothing to broadcast and nobody else's world moves. Sending it as an
 * edit-to-itself would put a no-op through the edit path — reach checks,
 * chunk deltas, the audit log — for something that is not an edit.
 *
 * ⚠️ **Deliberately general, and named for what it is.** The hearth being lit,
 * a chest being opened and a door being pushed are all this message with a
 * different block under the crosshair. What each block *does* is the server's
 * business, so a client cannot invent an interaction by asking for one.
 */
export interface UseBlockMessage {
  readonly type: 'use_block';
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * 🔨 **Make something** — T130 slice 3, **D95**.
 *
 * 🔴 **The client says WHICH RECIPE and WHERE IT IS STANDING, and nothing
 * else.** It does not say what it has, what it used or what it made. The server
 * looks all of that up, because a client that could name its own inputs could
 * name four stones it does not own.
 *
 * ⚠️ **`at` is the STATION, not the player.** For a recipe that needs a bench
 * it is the block the player is working at, and the server re-checks both that
 * it is in reach and that it really is that kind of block — **the same two
 * checks `use_block` already makes**, for the same reason.
 *
 * ✅ **`null` for a recipe made in your hands**, which is what `station: null`
 * means in `recipes.json` and is the one case that has to exist or nothing can
 * ever be made.
 */
/**
 * 🔱 **Thrust the spear at a fish** — T221, D115, protocol 31.
 *
 * 🎯 **haugur chose the verb:** *"Thrust — jab at a fish within reach."*
 *
 * 🔴 **THE CLIENT NAMES A FISH AND THAT IS A PROPOSAL, NOT A FACT.** It carries
 * one id and nothing else — not what was caught, not what it is worth, not that
 * it was in range. **The server runs {@link spearTargetFrom} against its own
 * copy of where everything is**, and a name that does not survive that is
 * refused.
 *
 * ⚠️ **This is the same rule the bug reporter's byline was built on:** *"the
 * browser never sends a NAME, it sends a credential"* — **a client that can
 * name its own target can name a fish on the other side of the bay.**
 *
 * ✅ **Why an id rather than a position:** a fish moves twenty times a second,
 * so a position would be stale before it arrived and the server would have to
 * guess which fish was meant. **An id cannot be ambiguous**, and it is already
 * on the wire because `creatures` carries one per animal.
 */
export interface SpearMessage {
  readonly type: 'spear';
  /** The creature id from the `creatures` message. */
  readonly id: string;
}

/**
 * 🙌 **Pick that one up** — T261, D123, protocol 37.
 *
 * 🎯 **haugur, 2026-09-23:** *"you can pick them up and put them in a pen if you
 * find them in the wild."*
 *
 * 🔴 **THE CLIENT NAMES AN ANIMAL AND THAT IS A PROPOSAL, NOT A FACT.** It
 * carries one id and nothing else. **The server re-runs `carryTargetFrom` — the
 * same function the client aimed with — from where it believes the player's eye
 * is**, and refuses a name that does not survive it. ⚠️ **A client that can name
 * its own target can name a sheep on the other side of the valley**, which is
 * the bug reporter's rule at a third subject: *the browser never sends a NAME,
 * it sends a credential.*
 *
 * ⚠️ **Nothing comes back on success.** The sheep simply stops appearing in the
 * next `creatures` beat and starts appearing as {@link PlayersMessage}'s
 * `carrying` — **two messages that already exist**, which is the same answer
 * `spear` gave.
 */
export interface CarryMessage {
  readonly type: 'carry';
  /** The creature id from the `creatures` message. ⚠️ **Checked, never believed.** */
  readonly id: string;
}

/**
 * 🙌 **Put it down** — T261, D123, protocol 37.
 *
 * 🔴 **IT CARRIES NOTHING AT ALL, AND THAT IS THE DESIGN.** Not where to put
 * it, not which animal: **the server already knows what you are carrying**, and
 * it works out where the sheep lands from where you are standing and which way
 * you face — `setDownAt` in `carry.ts`, which both ends import.
 *
 * ⚠️ **A position on this message would be a client naming a place**, and this
 * project has settled that three times now. **A sheep posted through a wall is
 * exactly what it would buy.**
 *
 * ⚠️ **It can be REFUSED** — there may be nowhere to stand. The server answers
 * with `edit_rejected`, the same way a spear that missed does.
 */
export interface ReleaseMessage {
  readonly type: 'release';
}

/**
 * 🛏️ **Get up** — D133, protocol 50. Any key while asleep sends it.
 *
 * 🔴 **It carries nothing.** The server knows which bed you are in and where
 * you stand up (`rumWakeSpot`), the same rule `release` keeps about a sheep.
 */
export interface WakeMessage {
  readonly type: 'wake';
}

/**
 * 🔪 **Strike that one with your knife** — D125, protocol 38.
 *
 * 🔴 **One id, checked and never believed** — the server re-runs
 * `knifeTargetFrom` from its own idea of your eye, refuses somebody else's
 * sheep, and decides whether this was a wound or the kill. **The client never
 * says which**; a client that could say *"killed"* could butcher a flock with
 * one click.
 */
/**
 * ✂️ **Shear that one** — D126, protocol 40.
 *
 * 🔴 **One id, checked and never believed**, exactly as {@link StrikeMessage}:
 * the server decides whether you hold a knife, whether the month lets you pluck
 * by hand, and whether the sheep is yours or wild. **The client never says
 * "shorn"**; it learns it from the next `creatures` beat.
 */
/** 🪢 **Put a lead on that sheep** — D128, protocol 42. One id, checked and never believed. */
export interface LeadMessage {
  readonly type: 'lead';
  readonly id: string;
}

/** 🪢 **Let that sheep off its lead** — D128, protocol 42. */
export interface UnleadMessage {
  readonly type: 'unlead';
  readonly id: string;
}

/** 🎣 **Cast the float where I am looking** — D129, protocol 43. No fields: the server aims. */
export interface CastMessage {
  readonly type: 'cast';
}

/** 🎣 **Pull** — land the fish if it is biting, otherwise wind the float in. D129. */
export interface ReelMessage {
  readonly type: 'reel';
}

/**
 * 🎁 **An admin asks for an item in their own pack** — protocol 47.
 *
 * ⚠️ **Shape only here.** Whether the sender is an admin, whether the key is an
 * item and whether the pack has room are the server's business.
 */
export interface GiveMessage {
  readonly type: 'give';
  /** An item KEY — the client turns a typed name into one (`give.ts`). */
  readonly item: string;
  /** 1 … `GIVE_MAX`. */
  readonly count: number;
}

/** 🌱 **Feed a sheep turf, to make it ready to mate** — D130, protocol 44. */
export interface FeedMessage {
  readonly type: 'feed';
  /** The creature id from the `creatures` message. ⚠️ **Checked, never believed.** */
  readonly id: string;
}

export interface ShearMessage {
  readonly type: 'shear';
  /** The creature id from the `creatures` message. ⚠️ **Checked, never believed.** */
  readonly id: string;
}

export interface StrikeMessage {
  readonly type: 'strike';
  /** The creature id from the `creatures` message. ⚠️ **Checked, never believed.** */
  readonly id: string;
}

export interface CraftMessage {
  readonly type: 'craft';
  /** The recipe's key in `recipes.json`. */
  readonly recipe: string;
  /** The station block being worked at, or `null` for your bare hands. */
  readonly at: { readonly x: number; readonly y: number; readonly z: number } | null;
}

/**
 * 🔥 **Move something between your pack and a hearth** — T128, D96, protocol 16.
 *
 * 🎯 **haugur, 2026-08-27:** *"i would also want to be able to move wood and
 * chacoal from my onhand inventory with the mouse, that adds the role of
 * charcoal storage to the heath."*
 *
 * 🔴 **Why this is not {@link MoveItemMessage} with an extra field.**
 * `move_item` moves within `player_inventory`, and a hearth slot is a row in
 * `block_data` — **two different tables.** ⚠️ `Development/06_Traps.md`: *adding
 * a parameter where an OPTIONAL one already lives changes what old calls mean.*
 * A new optional `at` on `move_item` would make every existing call mean "and
 * no container", which is a meaning they never stated.
 *
 * 🔴 **And why each end NAMES its container.** Both containers start at slot 0.
 * A bare pair of integers would leave the reader to infer which table each
 * belongs to, and getting it wrong moves **the right count of the right item
 * into the wrong box** with nothing raised anywhere.
 *
 * ⚠️ **`at` is checked, never trusted** — the same rule {@link CraftMessage}
 * keeps: the server reads the block at that position, confirms it really is a
 * hearth and that the player is within reach. A client that could name its own
 * container could name one it is nowhere near.
 */
export interface MoveHearthItemMessage {
  readonly type: 'move_hearth_item';
  /** Which hearth. ⚠️ Verified against the world, never believed. */
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
  readonly from: { readonly where: 'hearth' | 'player'; readonly slot: number };
  readonly to: { readonly where: 'hearth' | 'player'; readonly slot: number };
  /** How many. Fewer than the whole stack is how a stack is **split**. */
  readonly count: number;
}

/**
 * 🔥 **What a hearth is holding** — sent when its screen opens and after every
 * change (T128).
 *
 * 🔴 **Nothing sent this before.** The hearth's contents lived only in
 * `block_data` and no message carried them, which is why the menu could not be
 * built without a protocol bump.
 *
 * ⚠️ **Numbers and a flag, never a sentence.** Protocol 8's rule: the server
 * does not know what language this player reads, so it sends how much wood is
 * left and the client writes *"Burning — about 5 hours of wood left"*.
 */
export interface HearthContentsMessage {
  readonly type: 'hearth_contents';
  /**
   * 🔥 **This fire is the pit-house oven** — protocol 52, D135. Wood only, no
   * pot and no shelf, and never a home: the screen shows the two wood slots.
   * ⚠️ Absent for a hearth.
   */
  readonly oven?: true;
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
  /**
   * 🔥 **Eight counts: 0–1 fuel, 2–5 output, 6–7 what is to be cooked.**
   * Order is meaning — **protocol 32, D121.**
   *
   * ⚠️ **It was SIX until 2026-09-22, and the two new ones are APPENDED.** The
   * cooking slots are drawn at the top left of haugur's screen and numbered
   * last, because **a slot number is an identity on the wire and never a
   * position on the glass.** Renumbering would have put the right item in the
   * wrong box for anybody who had not caught up.
   */
  readonly slots: readonly number[];
  /**
   * 🔥 **WHICH thing is in each of the eight slots** — `null` for an empty one.
   *
   * 🔴 **Added as `woodItems` on 2026-09-02 because the panel was LYING.** Only
   * counts were sent, so the client had to guess what a count was OF and guessed
   * birch. 🎯 haugur, having fed a fire driftwood: *"it lights the fire but if i
   * open it up i dont see the rekaviður there, i placed 2 but there where not 2
   * on stock in the hearth."*
   *
   * 🔴 **It covered only the WOOD slots, and the reason it gave was *"charcoal
   * is charcoal"* — TRUE WHEN IT WAS WRITTEN AND FALSE SINCE D121.** The output
   * shelf holds cooked fish now, so a client working the item out from the slot
   * NUMBER would have drawn charcoal over somebody's dinner. ⚠️ **The comment
   * was not wrong; the world moved underneath it** — which is this repo's
   * commonest fault and the reason the field is now named for all eight rather
   * than for the group that happened to need it first.
   *
   * ⚠️ **Optional on the wire**, so a reader that has not caught up ignores it
   * rather than failing to parse.
   */
  readonly slotItems?: readonly (string | null)[];
  /** Whether it is alight. The wood slots being empty is the other signal. */
  readonly lit: boolean;
  /** Game hours of wood remaining. 0 when it is out. */
  readonly hoursLeft: number;
  /**
   * 🏠 **Is this the hearth you would wake at?** Protocol 22, D108.
   *
   * ⚠️ **The client cannot work this out** — it is never told where its home
   * is, only, on dying, that it woke there. So the server says, per hearth, and
   * the panel turns it into *your home — you will wake here* or *light it to
   * make it your home*. ⚠️ **Optional on the wire**, exactly as `slotItems` is.
   */
  readonly home?: boolean;
}

/**
 * 📦 **Move something between your pack and a chest** — T135, D106, protocol 20.
 *
 * 🔴 **Why this is not {@link MoveHearthItemMessage} with the word changed.**
 * The wire shape is nearly identical and the RULES are not. A hearth slot holds
 * **a count of one known thing**, so it can never swap — whatever came out
 * would have nowhere to go. **A chest slot holds an arbitrary stack**, so a swap
 * is what a player expects, and the move follows `moveStack`'s rules instead.
 *
 * ⚠️ **Two ends can both be `chest`, and cannot both be `player`.** Nothing
 * else can tidy the inside of a chest, while {@link MoveItemMessage} already
 * owns the pack — and two messages half-answering one question is how they
 * drift apart.
 *
 * ⚠️ **`at` is checked, never trusted.** The server reads the block at that
 * position, confirms it really is a chest and that the player is within reach.
 */
export interface MoveChestItemMessage {
  readonly type: 'move_chest_item';
  /** Which chest. ⚠️ Verified against the world, never believed. */
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
  readonly from: { readonly where: 'chest' | 'player'; readonly slot: number };
  readonly to: { readonly where: 'chest' | 'player'; readonly slot: number };
  /** How many. Fewer than the whole stack is how a stack is **split**. */
  readonly count: number;
}

/**
 * 📦 **What a chest is holding** — sent when its screen opens and after every
 * change (T135, protocol 20).
 *
 * 🔴 **Full stacks, not counts**, and that is the whole difference from
 * {@link HearthContentsMessage}. A hearth sends eight numbers because the ORDER
 * carries the meaning — 0–1 fuel, 2–5 output, 6–7 what is to be cooked. A chest
 * slot can hold anything, so a number alone says nothing at all, and the item
 * key has to travel with it.
 *
 * ⚠️ **That is the fault protocol 19 had just finished fixing on the hearth**,
 * where counts alone made the panel guess what a count was OF, and it guessed
 * birch while the fire was burning driftwood. 🎯 haugur found it in play. The
 * chest is built the right way round from the start because of it.
 *
 * ⚠️ **Absent slots are empty slots.** Only what is actually in the chest is
 * sent, so an empty chest is an empty array rather than 27 zeroes.
 */
/**
 * 🎣 **Every float in the water** — protocol 43, D129. Sent to everybody, the
 * caster included, whenever the list changes.
 */
export interface FloatsMessage {
  readonly type: 'floats';
  readonly floats: readonly {
    /** Who cast it — a connection id, matched like `ledBy`. */
    readonly by: string;
    readonly x: number;
    readonly y: number;
    readonly z: number;
    /** 🫧 Under — a fish is biting, pull now. */
    readonly dip: boolean;
  }[];
}

/** 🧵 What a vefstofa is doing — protocol 41, D127. */
export type SpinView = 'spinning' | 'idle' | 'full';

/**
 * 🧵 **What a loom is doing — protocol 53, D136.** `doing` is `loom.ts`'s
 * `LoomDoing`; `ells` is how much of the piece on the loom is woven (0–5);
 * `part` is how far into the step under way it is, 0–1, for the bar.
 */
export interface LoomView {
  readonly doing: 'idle' | 'warping' | 'weaving' | 'noWeft' | 'clothFull';
  readonly ells: number;
  readonly part: number;
}

export interface ChestContentsMessage {
  readonly type: 'chest_contents';
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
  /**
   * 🏠 **How many slots this container has** — protocol 23. 27 for a chest, 54
   * for a storehouse door; the client sizes the screen by it. ⚠️ Optional on
   * the wire; absent means 27.
   */
  readonly capacity?: number;
  /**
   * 🪧 **What this container is called** — protocol 24. The `en.json` key from
   * its `ContainerRules`: `chest.title`, `bur.title` or `skemma.title`.
   *
   * ⚠️ **Optional on the wire; absent means fall back to the slot count**, which
   * is what the client did before 2026-09-04 and is wrong for a skemma.
   */
  readonly title?: string;
  /** 📦 What is in it. Slot numbers are 0–26; absent means empty. */
  readonly stacks: readonly {
    readonly slot: number;
    readonly itemKey: string;
    readonly count: number;
  }[];
  /**
   * 🥫🔥 **This container's own FIRE, if it has one** — protocol 33, D121.
   *
   * 🎯 **haugur, 2026-09-22, asked where cooking happens:** *"3 and 4"* — the
   * hearth **and** a fire inside the búr — then *"same kinda o menu for the
   * hearth also, should work the same."*
   *
   * 🔴 **Absent means the container has no fire**, which is every chest and
   * every skemma, and is the ordinary case. **Only a búr has one.**
   *
   * ⚠️ **The shape is the HEARTH's, field for field** — eight slots, their item
   * names, whether it is alight and how long the wood lasts — because it **is**
   * a hearth: the same `resolveHearth` runs it, with `BUR_FIRE_RULES` instead of
   * `HEARTH_RULES`. 🔴 **The one difference is that it makes NO charcoal**, and
   * that difference is **D71**: a búr refuses everything which is not food, so a
   * búr filling with charcoal would be a locked rule broken from the inside.
   *
   * ⚠️ **There is no `home` here.** A búr's fire is not a hearth you wake at
   * (D108) and does not warm you (**D64** — 🎯 *"No — it only cooks"*). **A
   * field that is always false would read as a promise that it might one day be
   * true.**
   */
  readonly fire?: {
    /** Eight counts: **0–1 fuel, 2–5 output, 6–7 what is to be cooked.** */
    readonly slots: readonly number[];
    /** Which thing is in each of the eight, `null` for empty. */
    readonly slotItems: readonly (string | null)[];
    readonly lit: boolean;
    /** Game hours of wood remaining. 0 when it is out. */
    readonly hoursLeft: number;
  };
  /**
   * 🧵 **A vefstofa's spinning — protocol 41, D127.** Absent for every other
   * container. ✅ **While it says `spinning`, the screen asks again** with
   * `ask_hearth_contents`, exactly as a búr with a burning fire does.
   */
  readonly spin?: SpinView;
  /**
   * 🧵 **A loom's weaving — protocol 53, D136.** Absent for every other
   * container. ✅ **While it is `warping` or `weaving`, the screen asks again**
   * with `ask_hearth_contents`, as it does for spinning.
   */
  readonly loom?: LoomView;
}

/**
 * 🔨 **"What can the bench see from here?"** — T172, D111, protocol 25.
 *
 * 🔴 **It carries nothing at all, and that is the design.** The server answers
 * from `session.at` — **the same position the `craft` message is judged
 * from** — so the list on screen and the list the craft actually spends from
 * are two answers to one question. ⚠️ **A client that named its own position
 * could stand beside a stranger's storehouse** and read what is in it.
 *
 * 👉 **Sent when the craft screen opens, and again after a craft**, because a
 * craft is the one thing that certainly changed what is in reach.
 */
export interface AskNearbyMaterialsMessage {
  readonly type: 'ask_nearby_materials';
}

/**
 * 🍲 **"What is in this fire NOW?"** — T222, D121, protocol 32.
 *
 * 🔴 **The fire is the first container in this game that changes WHILE YOU ARE
 * LOOKING AT IT**, and that is the whole reason this message exists. A chest
 * changes only when somebody moves something; a hearth used to make one
 * charcoal every **five real minutes**, so nobody ever stood and watched.
 * **Cooking a fish takes about nineteen seconds** — 🎯 haugur's own sixteen to a
 * log — **so a player WILL stand and watch, and until now the screen would not
 * have moved.** ⚠️ **A fire visibly doing nothing is a bug report**, and it is
 * the same failure D90's charcoal refusal was given words for.
 *
 * 🔴 **It names the hearth, and the server checks the name.** Unlike
 * {@link AskNearbyMaterialsMessage}, which carries nothing because the server
 * already knows where you are, this has to say WHICH fire — you can stand
 * between two. ⚠️ **So the server verifies it**: that the block there really is
 * a hearth, and that it is {@link withinReach} of `session.at`. **A client that
 * could name any position could read the inside of somebody else's fire from
 * across the valley.**
 *
 * 🔴 **It can change the world, and that is not a mistake.** Reading a fire
 * RESOLVES it — the burning and the cooking are worked out when somebody looks,
 * which is this game's oldest arrangement and the reason a fire does not tick.
 * ⚠️ **So this is not a pure read**, and it must be safe to send repeatedly:
 * resolving twice in the same instant is the same answer, because everything is
 * measured from an absolute clock rather than accumulated.
 */
export interface AskHearthContentsMessage {
  readonly type: 'ask_hearth_contents';
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
}

/**
 * 🥫🔥 **Move something between a búr's FOOD STORE and its own FIRE** — T222c,
 * D121, protocol 34.
 *
 * 🎯 **haugur, the hour the búr's fire shipped:** *"fire tab works fine but my
 * fish are in the other tab, so if i want to cook them, i have to first drag
 * them to onhand switch to fire and drag them there."*
 *
 * 🔴 **It is its own message because it is the pair nobody had.** `move_item`
 * is pack-to-pack, `move_chest_item` is pack-to-store and `move_hearth_item` is
 * pack-to-fire — **every one of them has the player's pack at an end.** This is
 * the only move on that screen with the pack at NEITHER, and widening one of
 * the others to reach it would make a message that half-answers two questions.
 * ⚠️ **T247 is what that costs**: a screen sending a move to the function that
 * is not allowed to do it, refused in silence.
 *
 * ✅ **And it is cheap, because both halves are ONE `block_data` row** — one
 * read and one write, with no chance of writing one without the other.
 *
 * 🔴 **The server decides everything.** That the block really is a búr, that it
 * is in reach, and both ends' own rules: **the fire's input takes only what can
 * be cooked, and the store takes only food (D71).** A client naming a chest or
 * a hearth here is refused.
 */
export interface MoveBurItemMessage {
  readonly type: 'move_bur_item';
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
  /** 🥫 `store` is the 54 food slots; 🔥 `fire` is the eight. */
  readonly from: { readonly where: 'store' | 'fire'; readonly slot: number };
  readonly to: { readonly where: 'store' | 'fire'; readonly slot: number };
  readonly count: number;
}

export type ClientMessage =
  | HelloMessage
  | ChunkRequestMessage
  | PlayerStateMessage
  | BlockEditMessage
  | SelectSlotMessage
  | MoveItemMessage
  | CraftMessage
  | DiscardItemMessage
  | EatItemMessage
  | UseBlockMessage
  | SpearMessage
  | StrikeMessage
  | ShearMessage
  | FeedMessage
  | GiveMessage
  | CastMessage
  | ReelMessage
  | LeadMessage
  | UnleadMessage
  | CarryMessage
  | ReleaseMessage
  | WakeMessage
  | MoveHearthItemMessage
  | MoveChestItemMessage
  | AskNearbyMaterialsMessage
  | AskHearthContentsMessage
  | MoveBurItemMessage;

/**
 * 💀 **You died, and here is where you woke up** — D21, D82, protocol 21.
 *
 * 🔴 **The server decides all of it and the client is told.** Where you wake,
 * what you kept and what your bars read are not things a client may work out
 * for itself — `Development/CLAUDE.md` rule 6 — and a client that could name
 * its own respawn point could name one inside a mountain of iron.
 *
 * ⚠️ **The inventory is NOT in here.** Death empties your carried slots, and the
 * server sends the ordinary `inventory` message for that — one way of changing
 * a pack, not two. This message is about the death itself.
 */
export interface DiedMessage {
  readonly type: 'died';
  /** Where the player now is, in ISN93 metres. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /**
   * 🏠 **Did they wake at their own hearth, or back at the beginning?**
   *
   * 🔴 **What the message on screen reads from.** *"You woke at your hearth"*
   * to somebody who has never lit one would be a lie, and the client cannot
   * tell the difference by looking at the coordinates.
   */
  readonly atHome: boolean;
}

/**
 * 🏠 **Where home is** — T201, protocol 29.
 *
 * 🎯 **haugur, 2026-09-14:** *"add Back to Home button on the map that returns
 * you to your hearth."*
 *
 * 🔴 **Protocol 22 said whether THIS hearth is home; it never said WHERE home
 * is.** That is enough for a line in the hearth panel and not enough for a
 * button, and the gap is the whole reason this exists rather than a wider
 * `hearth_contents`.
 *
 * ⚠️ **Sent on connect AND whenever it changes**, because lighting a hearth
 * moves it. A field on the welcome would be right at the start of the session
 * and quietly wrong for the rest of it — *a second way in that forgets what the
 * first one does*, recorded seven times in this repo. ✅ **One helper on the
 * server sets `session.home` and sends this**, so the two cannot disagree.
 *
 * 🔴 **`at: null` is an ORDINARY answer, not a failure.** Somebody who has
 * never lit a hearth has no home; on development, where `players` holds no
 * rows, it is `null` on every connect until a fire is lit. ⚠️ **The client must
 * draw that as a reason — *light a hearth first* — and never as a button that
 * does nothing**, which is the choice the menu's Snow switch already makes.
 */
export interface HomeMessage {
  readonly type: 'home';
  /**
   * The hearth the player would wake at, in ISN93 metres, or `null`.
   *
   * ⚠️ **`y` is the HEARTH's own block**, the position `saveHome` wrote — not
   * where a body would stand. The client adds its own clearance when it goes
   * there, the same way the map's teleport already does for any other point.
   */
  readonly at: { readonly x: number; readonly y: number; readonly z: number } | null;
}

/**
 * 🔨 **Everything the bench can see, for the SCREEN** — T172, D111, protocol 25.
 *
 * 🔴 **THIS IS A PICTURE, NOT AN ANSWER.** The server recomputes every input
 * from scratch when the `craft` arrives, so a list that has gone stale can only
 * ever produce a refusal — never a craft that takes from a chest that is not
 * there. ⚠️ **Nothing here may be believed back**: the client is not told this
 * so it can prove anything, only so it can stop lying to the player.
 *
 * ⚠️ **POOLED, WITH NO SLOT NUMBERS.** `pooledStacks` flattens every container
 * into one list because `canMake` counts by item key and never looks at a slot.
 * 🔴 **A pack's slots 34–37 are clothing and a búr's 34–37 are ordinary
 * storage**, so the two numberings mean different things and merging them is
 * sound for counting and for nothing else. **Do not hand this to anything that
 * moves an item.**
 */
export interface NearbyMaterialsMessage {
  readonly type: 'nearby_materials';
  /**
   * 📦 What is in every container in reach, pooled. ⚠️ **The pack is NOT in
   * here** — the client already has that, and sending it twice would count
   * everything the player is carrying twice over.
   */
  readonly stacks: readonly {
    readonly itemKey: string;
    readonly count: number;
  }[];
  /**
   * How many containers those came out of, so the screen can say where a
   * material is. ⚠️ **Zero is a real answer** — no containers in reach, or the
   * lookup failed and the server carried on with the pack alone, exactly as
   * `craft` does.
   */
  readonly containers: number;
}

/**
 * 🧍 **Where everybody else is** — T173, protocol 26.
 *
 * 🎯 **haugur, 2026-09-07:** *"we need to build a caracter so people can see
 * each other in the game. if not people would only see block appearing but no
 * one building them."*
 *
 * 🔴 **The first message on this wire about somebody OTHER than the player it
 * is sent to.** The world, the pack, the body, the weather and what is in reach
 * were all about you; this says *there is another person over there*.
 *
 * ⚠️ **Everyone but the recipient.** A client is never in its own list, so it
 * never has to work out which entry is itself and can never draw a body inside
 * its own head.
 *
 * ⚠️ **`y` is the FEET.** It is the same number `player_state` reports — the
 * camera sits `EYE_HEIGHT` above it — so a body stands on this rather than
 * hanging from it.
 *
 * ⚠️ **An empty list is a real answer and must be sent**, or the last person to
 * walk away stays standing there forever.
 */
export interface PlayersMessage {
  readonly type: 'players';
  readonly players: readonly {
    /**
     * Which connection this is, stable for as long as it lasts.
     *
     * 🔴 **A connection, not an account.** Development has no accounts at all,
     * and two windows open on one account are two people as far as this is
     * concerned. ⚠️ **It is not a name and must never be shown** — it exists so
     * a client can tell one body from another between beats.
     */
    readonly id: string;
    /**
     * 🧍 **What to call them on screen** — T176, protocol 27, or `null` for a
     * connection with no account behind it.
     *
     * 🔴 **`null` is the ORDINARY case on development and must draw nothing.**
     * Dev runs anonymously with no `users` rows at all, so every body there has
     * no name. ⚠️ **A head labelled *Anonymous* is worse than a bare head** —
     * it invents a person, and it would be the same invented person on every
     * body on screen.
     *
     * ⚠️ **This is the account's `display_name`, so it is NOT unique per body**
     * — two windows on one account are two bodies wearing one name, which is
     * true rather than a bug. {@link id} is what tells bodies apart.
     *
     * ⚠️ **Optional on the wire.** An older client ignores it and draws exactly
     * the nameless bodies it drew before.
     */
    readonly name?: string | null;
    readonly x: number;
    readonly y: number;
    readonly z: number;
    /** Which way they are facing, in radians — the same yaw `player_state` sends. */
    readonly yaw: number;
    /**
     * 🙌 **The species of animal in their arms, or absent for empty-handed** —
     * T261, protocol 37.
     *
     * 🔴 **THE KIND, NEVER THE ID.** Everybody watching needs to draw *a sheep*
     * in somebody's arms; **nobody needs to know WHICH sheep**, and an id here
     * would put a creature's identity on a message about people. ⚠️ **That is
     * the same rule `creatures` keeps about its own id** — it is for telling
     * bodies apart between beats and for nothing else.
     *
     * ⚠️ **A carried animal is ABSENT from the `creatures` message**, because
     * it is not standing anywhere. 🔴 **A body left at its last position would
     * be a ghost**, and one drawn at the carrier's feet would be a sheep being
     * dragged.
     *
     * ⚠️ **Optional on the wire**, so an older client draws exactly the
     * empty-handed bodies it drew before.
     */
    readonly carrying?: string | null;
    /**
     * 🐣 **How big the animal in their arms is, if it is a lamb** — protocol 49.
     * A fraction of an adult, the same number `creatures[].lamb` carries.
     * ✅ **Absent means grown**, so an older server draws an adult.
     */
    readonly carryingLamb?: number;
    /**
     * 🛏️ **Lying in a bed** — protocol 50, D133. Present only for a sleeper:
     * `x y z` is then the middle of the mattress and `yaw` runs along the bed,
     * so the body is drawn lying down there. ⚠️ **A player who logged out asleep
     * is in the list too**, with an id that is not a connection.
     */
    readonly lying?: true;
  }[];
}

/**
 * 🐑 **Every animal near you, and what each one is doing** — T226, D117,
 * protocol 30.
 *
 * 🔴 **The first message about something alive that is not a person.**
 */
export interface CreaturesMessage {
  readonly type: 'creatures';
  readonly creatures: readonly {
    /**
     * Which animal this is, stable for its life.
     *
     * ⚠️ **Never shown to anybody** — it exists so a client can tell one animal
     * from another between beats, and so it can smooth a body's movement
     * instead of teleporting it. **The same rule as a player's connection id.**
     */
    readonly id: string;
    /** Which species — `thorskur`, `kind`. The key the body is drawn from. */
    readonly kind: string;
    readonly x: number;
    /** ⚠️ **The FEET**, the same convention `players` uses. */
    readonly y: number;
    readonly z: number;
    /** Which way it faces, radians, 0 = +x — the same convention as a player's yaw. */
    readonly yaw: number;
    /**
     * 🔴 **THE NAME OF THE BEHAVIOUR IN CHARGE, AND THIS FIELD IS WHY THE BRAIN
     * IS A FLAT LIST.**
     *
     * `Wander`, `Follow`, `Fleeing`, `Grazing`, or `null` for an animal doing
     * nothing. 🆕 **`Follow` since T233** — it is what a fish in a shoal says,
     * and it is how an F8 report can tell *the shoal scattered* from *the shoal
     * never formed*.
     *
     * 👉 **It goes into the F8 bug report**, so a report can say *"the sheep was
     * `Fleeing` when it walked through the wall"* — which is a sentence
     * somebody can act on, and the thing a behaviour tree or a weighted blend
     * could not produce.
     *
     * ⚠️ **Not shown to a player in the ordinary HUD.** It is an instrument, and
     * an instrument on screen becomes a setting somebody turns off.
     */
    readonly doing: string | null;
    /**
     * 👀 **What it is LOOKING at, by behaviour name** — protocol 35, T255.
     *
     * 🔴 **{@link doing} CANNOT CARRY THIS, AND THAT IS THE WHOLE REASON THIS
     * FIELD EXISTS.** `doing` is the behaviour holding `move`; **a look
     * behaviour holds only `look`**, so `Watch` never appeared on the wire at
     * all.
     *
     * ⚠️ **That made the sheep's head DEAD CODE.** It was cut, pivoted and
     * animated, and then asked to turn toward a target that was always zero —
     * 🔴 **a fault no sample sheet could ever have shown**, because the
     * geometry was right and the animation was right and nobody was driving it.
     *
     * 📖 `Minecraft/18` §1.3 is why the two are separate in the first place:
     * ***a cow that walks somewhere while turning its head to watch you is two
     * goals that have never heard of each other*** — *the difference between an
     * animal and a vehicle.* 👉 **Two flags on the brain, two names on the
     * wire.**
     *
     * ⚠️ **The SERVER decides, not the client.** A client that invented *"is it
     * watching me"* would have two players seeing two different animals, and
     * *state the client invents is state that gets forgotten* — D108's lesson.
     */
    readonly looking: string | null;
    /**
     * 👀 **WHERE it is looking, in world metres — protocol 36, T255.**
     *
     * 🔴 **{@link looking} SAYS WHY AND THIS SAYS WHERE**, and the client needs
     * both: a name cannot be turned into an angle without guessing.
     *
     * ⚠️ **The first version had the CLIENT guess** — *if it says `Watch`, turn
     * it toward me* — and that is wrong twice over. **Two players would see the
     * sheep looking at themselves**, and 🔴 **an idle look-around has no target
     * a client could invent at all.**
     *
     * ✅ **The brain already decides this.** `Intent.lookAt` has existed since
     * T226 and was simply never sent. 👉 ***The server owns behaviour; the
     * client owns drawing.***
     *
     * ⚠️ **`null` means *straight ahead*, which is a decision** — a creature
     * with nothing to look at faces the way it is going.
     */
    readonly lookAt: { readonly x: number; readonly y: number; readonly z: number } | null;
    /**
     * 🩸 **How many blows have LANDED on it, ever** — protocol 39.
     *
     * 🎯 **haugur, 2026-09-28:** *"there need to be somekind of indication that
     * im hurting it, like it flashing red when i hit it."* ✅ **A client flashes
     * the animal red when this goes UP.** A count rather than a flag, because a
     * flag that is on for one beat can fall between two beats and be missed.
     * ⚠️ **It never goes down** — healing resets the strikes toward a kill, not
     * this. **Absent means never hit**, and an older client ignores it.
     */
    readonly hits?: number;
    /**
     * ✂️ **Shorn, and the wool not back yet** — protocol 40, D126.
     *
     * ✅ **A client draws the shorn body while this is `true`.** ⚠️ **Absent
     * means woolly** — never `false` on the wire — so an older client, and
     * every sheep that was never shorn, costs nothing.
     */
    readonly shorn?: true;
    /** 🦊🐑 A fox with a lamb in its jaws — protocol 51. Absent means none. */
    readonly prey?: true;
    /**
     * 🪢 **Who is leading it — a connection id, or absent** — protocol 42, D128.
     * Matched against `players[].id`, or `welcome.you` for your own hand.
     */
    readonly ledBy?: string;
    /** 🪵 **The peg it is tied to, as a cell, or absent** — protocol 42, D128. */
    readonly tether?: { readonly x: number; readonly y: number; readonly z: number };
    /**
     * 🐣 **A lamb, and how big — a fraction of an adult's height, or absent for
     * a grown sheep** — protocol 44, D130. 0.55 at birth, 0.85 the day before
     * it is grown on 1 October. ✅ **Absent means grown**, so an older client
     * draws a lamb full size and costs nothing else.
     */
    readonly lamb?: number;
    /**
     * 💙 **Ready to mate — fed turf in the last thirty seconds** — protocol 45,
     * D130. ✅ **Absent means not ready**, never `false` on the wire.
     */
    readonly ready?: true;
    /**
     * ❤️ **Carrying, or mated this season — it will not take the turf** —
     * protocol 46, D130. ✅ **Absent means it may still mate.**
     */
    readonly mated?: true;
    /**
     * 🥣 **How a KEPT sheep is doing** — protocol 48, T286. Absent when fed, and
     * always absent for a wild one, which never goes hungry. Looking at your own
     * sheep says so.
     */
    readonly hunger?: 'hungry' | 'starving';
  }[];
}

/**
 * 🐑⚠️ **YOUR SHEEP ARE STARVING — to ONE player** — protocol 48, T286.
 *
 * 🎯 **haugur:** *"we need to find a clever way to warn a player of imminent
 * death of his animals, a short note in hud will not cut it"* — and he chose
 * **an arrow at the screen edge** pointing at the flock, redder as death nears,
 * **and a warning at login** with a button to the World map.
 *
 * 🔴 **Only the owner's own sheep, only to the owner.** Sent when the list
 * CHANGES and once at every login, so the warning is waiting when they arrive.
 * An empty list means nothing is starving — the arrow goes away.
 */
export interface FlockWarningMessage {
  readonly type: 'flock_warning';
  readonly flocks: readonly {
    /** Where the starving sheep are — the middle of them, in world metres. */
    readonly x: number;
    readonly z: number;
    /** How many are starving there. */
    readonly count: number;
    /** Game days the worst of them has left before it dies. */
    readonly daysLeft: number;
  }[];
}

/**
 * 🙌 **What is in YOUR OWN arms** — T261, protocol 37.
 *
 * ## 🔴 WHY THIS EXISTS WHEN `players` ALREADY CARRIES `carrying`
 *
 * **`players` never includes the viewer.** `playersVisibleTo` drops you from
 * your own list on purpose — *"a client that had to find itself in its own list
 * would eventually get it wrong, and the failure is a body standing inside your
 * own head that follows you everywhere."*
 *
 * 👉 **So the one person who cannot learn what you are carrying from `players`
 * is you.** This is that gap, and nothing else.
 *
 * ⚠️ **The alternative was the client REMEMBERING that it pressed the key**, and
 * this project does not predict: an optimistic `carrying` that the server had
 * refused would leave the HUD saying *"put it down"* while the sheep stood in
 * the field. 🔴 **The same reason the client does not predict its own block
 * edits.**
 *
 * ⚠️ **Sent only when it CHANGES**, like every other beat here.
 */
/**
 * 🛏️ **You are asleep, in this bed — or awake again** — D133, protocol 50. To
 * the sleeper alone; `players` never includes yourself.
 *
 * ⚠️ **`bed: null` comes with `wakeAt`**: where you stand up, beside the bed,
 * which the client moves you to. The server decides it, never the client.
 */
export interface AsleepMessage {
  readonly type: 'asleep';
  /** The bed's head cell, or `null` when you are awake. */
  readonly bed: { readonly x: number; readonly y: number; readonly z: number } | null;
  /** Which way the bed lies (the head's facing), while asleep. */
  readonly facing?: 'north' | 'east' | 'south' | 'west';
  /** Where to stand, when waking. */
  readonly wakeAt?: { readonly x: number; readonly y: number; readonly z: number };
}

export interface CarryingMessage {
  readonly type: 'carrying';
  /** The species in your arms, or `null` for empty-handed. */
  readonly kind: string | null;
  /**
   * 🐣 **A lamb, and how big** — protocol 49, the same fraction as
   * `creatures[].lamb`. ✅ **Absent means grown.**
   */
  readonly lamb?: number;
}

export type ServerMessage =
  | FlockWarningMessage
  | WelcomeMessage
  | DiedMessage
  | HomeMessage
  | ChunkErrorMessage
  | BlockUpdateMessage
  | EditRejectedMessage
  | WorldClockMessage
  | WorldWeatherMessage
  | InventoryMessage
  | VitalsMessage
  | HearthContentsMessage
  | ChestContentsMessage
  | NearbyMaterialsMessage
  | PlayersMessage
  | CreaturesMessage
  | CarryingMessage
  | AsleepMessage
  | FloatsMessage;

/**
 * How many chunks one request may ask for.
 *
 * A rate limit, not a tuning knob: a runaway client loop must not be able to
 * take the world down for everyone else (standards §3).
 */
export const MAX_CHUNKS_PER_REQUEST = 64;

/**
 * 📷 **What format a bug report's screenshot is sent in** — T174, T182.
 *
 * 🔴 **IT LIVES HERE BECAUSE THE TWO HALVES DISAGREED FOR THREE DAYS AND
 * NOTHING SAID SO.** T182 changed the browser to JPEG so the picture would be
 * small enough to send; the server went on reading `data:image/png;base64,` and
 * **discarded every screenshot, silently, by design.** ⚠️ **F8 had never once
 * attached a picture** — refused as too large before that change, dropped after
 * it — and `bug_filed { shot: false }` reads exactly like a player who sent
 * none. 🎯 **Found when haugur filed two reports about a hole at Gullfoss and
 * neither carried the thing he was pointing at.**
 *
 * ✅ **The client passes this to `canvas.toDataURL` and the server accepts this
 * prefix.** Neither restates it, so they cannot drift apart again.
 *
 * ⚠️ **JPEG rather than PNG, and the alpha is why that is safe:** the 3D canvas
 * is opaque and the HUD is drawn over it in the DOM, so there is no
 * transparency to lose. If that stops being true, transparent areas come out
 * black.
 */
export const BUG_SHOT_TYPE = 'image/jpeg';

/** The `data:` header a {@link BUG_SHOT_TYPE} picture arrives behind. */
export const BUG_SHOT_PREFIX = `data:${BUG_SHOT_TYPE};base64,`;

// ── Binary chunk frames ─────────────────────────────────────────────────────

export const FRAME_CHUNK_DATA = 1;

/**
 * Header size in bytes.
 *
 * 16, not 15, so the block data starts 2-byte aligned and can be read as a
 * `Uint16Array` view directly onto the received buffer — no copy. An
 * unaligned offset throws `RangeError` at runtime, which is a confusing way to
 * discover a layout decision.
 */
export const CHUNK_FRAME_HEADER_BYTES = 16;

const FLAG_EMPTY = 1;

/**
 * Encode a chunk as a binary frame.
 *
 * An empty chunk — all air, which is most of the sky — is sent as a 16-byte
 * header with no payload at all.
 */
export function encodeChunkFrame(
  coord: ChunkCoord,
  blocks: Uint16Array,
  worldgenVersion: number,
  empty: boolean,
): ArrayBuffer {
  if (!empty && blocks.length !== BLOCKS_PER_CHUNK) {
    throw new RangeError(`chunk has ${blocks.length} blocks, expected ${BLOCKS_PER_CHUNK}`);
  }

  const payloadBytes = empty ? 0 : blocks.byteLength;
  const buffer = new ArrayBuffer(CHUNK_FRAME_HEADER_BYTES + payloadBytes);
  const view = new DataView(buffer);

  view.setUint8(0, FRAME_CHUNK_DATA);
  view.setUint8(1, empty ? FLAG_EMPTY : 0);
  view.setUint16(2, worldgenVersion, true);
  view.setInt32(4, coord.cx, true);
  view.setInt32(8, coord.cy, true);
  view.setInt32(12, coord.cz, true);

  if (!empty) {
    new Uint16Array(buffer, CHUNK_FRAME_HEADER_BYTES).set(blocks);
  }

  return buffer;
}

export interface DecodedChunkFrame {
  readonly coord: ChunkCoord;
  readonly worldgenVersion: number;
  readonly empty: boolean;
  /** All-air chunks decode to a zero-filled array, so callers need no special case. */
  readonly blocks: Uint16Array;
}

/**
 * Decode a binary chunk frame.
 *
 * Throws on anything malformed rather than returning a half-built chunk. A
 * truncated frame that silently became a chunk of air would punch a hole in the
 * world that looks like a worldgen bug.
 */
export function decodeChunkFrame(buffer: ArrayBuffer): DecodedChunkFrame {
  if (buffer.byteLength < CHUNK_FRAME_HEADER_BYTES) {
    throw new RangeError(`frame is ${buffer.byteLength} bytes, shorter than its header`);
  }

  const view = new DataView(buffer);
  const kind = view.getUint8(0);
  if (kind !== FRAME_CHUNK_DATA) {
    throw new RangeError(`unknown binary frame type ${kind}`);
  }

  const empty = (view.getUint8(1) & FLAG_EMPTY) !== 0;
  const worldgenVersion = view.getUint16(2, true);
  const coord: ChunkCoord = {
    cx: view.getInt32(4, true),
    cy: view.getInt32(8, true),
    cz: view.getInt32(12, true),
  };

  if (empty) {
    return { coord, worldgenVersion, empty, blocks: new Uint16Array(BLOCKS_PER_CHUNK) };
  }

  const expected = CHUNK_FRAME_HEADER_BYTES + BLOCKS_PER_CHUNK * 2;
  if (buffer.byteLength !== expected) {
    throw new RangeError(`frame is ${buffer.byteLength} bytes, expected ${expected}`);
  }

  /* A view onto the received buffer, not a copy. */
  return {
    coord,
    worldgenVersion,
    empty,
    blocks: new Uint16Array(buffer, CHUNK_FRAME_HEADER_BYTES, BLOCKS_PER_CHUNK),
  };
}

// ── Inbound validation ──────────────────────────────────────────────────────

function isChunkCoord(v: unknown): v is ChunkCoord {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return Number.isInteger(c['cx']) && Number.isInteger(c['cy']) && Number.isInteger(c['cz']);
}

/**
 * ⚠️ `Number.isFinite`, not `typeof === 'number'`.
 *
 * `NaN` and `Infinity` are both numbers as far as `typeof` is concerned, and
 * both survive JSON as `null` — but a hand-built packet can carry them, and a
 * position of `NaN` compares false against every bound, so every range check
 * downstream passes by accident.
 */
function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * The longest a block key may be.
 *
 * Keys are short words — `mosahraun` is the longest at nine characters. The
 * limit is here so a client cannot make the server hold a megabyte string
 * looking it up in a twenty-nine entry Map.
 */
const MAX_BLOCK_KEY_LENGTH = 64;

/**
 * The longest a play token or game session may be.
 *
 * Both are base64url of 24 or 32 random bytes — 32 and 43 characters. The limit
 * is here so an unauthenticated client cannot make the server carry a megabyte
 * string into a database query.
 */
const MAX_CREDENTIAL_LENGTH = 128;

/**
 * Validate a message that arrived from a client.
 *
 * Returns `null` rather than throwing — a bad packet should disconnect one
 * client, not raise an exception through the server's message loop.
 */
export function parseClientMessage(raw: string): ClientMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) return null;
  const msg = value as Record<string, unknown>;

  if (msg['type'] === 'hello') {
    if (!Number.isInteger(msg['protocolVersion'])) return null;

    /* ⚠️ Shape only. Whether a credential is *valid* is the database's answer,
       not this layer's — but a credential of the wrong type or an absurd length
       should never reach a query at all. */
    const credential = (value: unknown): string | undefined =>
      typeof value === 'string' && value.length > 0 && value.length <= MAX_CREDENTIAL_LENGTH
        ? value
        : undefined;

    const token = credential(msg['token']);
    const session = credential(msg['session']);

    return {
      type: 'hello',
      protocolVersion: msg['protocolVersion'] as number,
      /* Strictly `true`, not truthy. A client sending `flat: "0"` — which a URL
         parameter very nearly is — must not get a debug world. */
      ...(msg['flat'] === true ? { flat: true } : {}),
      ...(token === undefined ? {} : { token }),
      ...(session === undefined ? {} : { session }),
    };
  }

  if (msg['type'] === 'chunk_request') {
    const chunks = msg['chunks'];
    if (!Array.isArray(chunks)) return null;
    if (chunks.length === 0 || chunks.length > MAX_CHUNKS_PER_REQUEST) return null;
    /* No cast needed: `every` with a type guard narrows the array itself. */
    if (!chunks.every(isChunkCoord)) return null;
    return { type: 'chunk_request', chunks };
  }

  if (msg['type'] === 'player_state') {
    const { x, y, z, yaw, pitch } = msg;
    if (![x, y, z, yaw, pitch].every(isFiniteNumber)) return null;
    if (typeof msg['flying'] !== 'boolean') return null;
    return {
      type: 'player_state',
      x: x as number,
      y: y as number,
      z: z as number,
      yaw: yaw as number,
      pitch: pitch as number,
      flying: msg['flying'],
    };
  }

  if (msg['type'] === 'block_edit') {
    /* ⚠️ Integers, not merely finite. A block coordinate of 12.5 is not a
       block, and `Math.floor`ing it here would silently accept a packet that
       means something the sender did not say. Reject it and let the client be
       fixed. */
    const { x, y, z } = msg;
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) return null;

    const block = msg['block'];
    if (typeof block !== 'string' || block.length === 0) return null;
    if (block.length > MAX_BLOCK_KEY_LENGTH) return null;

    /* Whether the key names a real block is decided by whoever holds the
       palette — this layer only guarantees the shape. */
    return { type: 'block_edit', x: x as number, y: y as number, z: z as number, block };
  }

  if (msg['type'] === 'select_slot') {
    /* ⚠️ Shape only, and integers rather than merely finite — slot 2.5 is not a
       slot. Whether the number is *in range* belongs to whoever owns the
       inventory rules, exactly as the block palette owns block keys. */
    const slot = msg['slot'];
    if (!Number.isInteger(slot)) return null;
    return { type: 'select_slot', slot: slot as number };
  }

  if (msg['type'] === 'discard_item') {
    /* ⚠️ Shape only, as everywhere else here. Whether the slot holds that many
       is the inventory's question — and it is the question that stops a stale
       client destroying an amount nobody asked for. */
    const { from, count } = msg;
    if (!Number.isInteger(from) || !Number.isInteger(count)) return null;
    return { type: 'discard_item', from: from as number, count: count as number };
  }

  if (msg['type'] === 'eat_item') {
    /* ⚠️ Shape only, as everywhere here. Whether that slot holds anything, and
       whether what it holds is food at all, is the server's question — and it
       is the question that stops a client eating a rock. */
    const slot = msg['slot'];
    if (!Number.isInteger(slot)) return null;
    return { type: 'eat_item', slot: slot as number };
  }

  if (msg['type'] === 'use_block') {
    /* ⚠️ Integers, exactly as `block_edit` — a coordinate of 12.5 is not a
       block, and flooring it here would accept a packet meaning something the
       sender did not say. What the block at that position DOES is the server's
       business; a client cannot invent an interaction by asking for one. */
    const { x, y, z } = msg;
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) return null;
    return { type: 'use_block', x: x as number, y: y as number, z: z as number };
  }

  if (msg['type'] === 'spear') {
    /* ⚠️ Shape only — a non-empty string. Whether that fish EXISTS, whether it
       is in reach and whether the player is even holding a spear are all the
       server's business, and all three are checked there. A bounded length
       because an id is a short token and nothing should be able to post a
       megabyte of one. */
    const id = msg['id'];
    if (typeof id !== 'string' || id.length === 0 || id.length > 64) return null;
    return { type: 'spear', id };
  }

  if (msg['type'] === 'cast') return { type: 'cast' };
  if (msg['type'] === 'reel') return { type: 'reel' };

  if (msg['type'] === 'lead' || msg['type'] === 'unlead') {
    /* 🪢 D128, protocol 42. ⚠️ Shape only — the hand, the reach and whose lead
       it is are the server's business. */
    const id = msg['id'];
    if (typeof id !== 'string' || id.length === 0 || id.length > 64) return null;
    return msg['type'] === 'lead' ? { type: 'lead', id } : { type: 'unlead', id };
  }

  if (msg['type'] === 'give') {
    /* 🎁 Protocol 47. ⚠️ Shape only — who may give is the server's call. */
    const item = msg['item'];
    const count = msg['count'];
    if (typeof item !== 'string' || item.length === 0 || item.length > 64) return null;
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 1 || count > GIVE_MAX) {
      return null;
    }
    return { type: 'give', item, count };
  }

  if (msg['type'] === 'feed') {
    /* 🌱 D130, protocol 44. ⚠️ Shape only, exactly as `shear` — the turf, the
       month, the pen and whose sheep it is are the server's business. */
    const id = msg['id'];
    if (typeof id !== 'string' || id.length === 0 || id.length > 64) return null;
    return { type: 'feed', id };
  }

  if (msg['type'] === 'shear') {
    /* ✂️ D126, protocol 40. ⚠️ Shape only, exactly as `strike` — the knife,
       the month, the reach and whose sheep it is are the server's business. */
    const id = msg['id'];
    if (typeof id !== 'string' || id.length === 0 || id.length > 64) return null;
    return { type: 'shear', id };
  }

  if (msg['type'] === 'strike') {
    /* 🔪 D125, protocol 38. ⚠️ Shape only, exactly as `spear` and `carry` —
       whose sheep it is, whether you hold a knife and whether it is in reach
       are all the server's business. */
    const id = msg['id'];
    if (typeof id !== 'string' || id.length === 0 || id.length > 64) return null;
    return { type: 'strike', id };
  }

  if (msg['type'] === 'carry') {
    /* 🙌 T261, protocol 37. ⚠️ **Shape only, exactly as `spear` above** —
       whether that animal exists, whether it can be carried at all, whether it
       is in reach and whether your hands are already full are every one of them
       the server's business, and every one is checked there. */
    const id = msg['id'];
    if (typeof id !== 'string' || id.length === 0 || id.length > 64) return null;
    return { type: 'carry', id };
  }

  if (msg['type'] === 'release') {
    /* 🙌 T261, protocol 37. 🔴 **It carries nothing**, so there is nothing to
       validate — the server knows what you are holding and works out where it
       lands. ⚠️ **A case that checks no fields still has to EXIST**: this
       parser is an allowlist, and a message missing from it is not rejected,
       **it hangs the socket up.** That is what `ask_nearby_materials` did. */
    return { type: 'release' };
  }

  if (msg['type'] === 'wake') {
    /* 🛏️ D133, protocol 50. Nothing to validate — and, like `release`, the
       case must EXIST or the allowlist hangs the socket up. */
    return { type: 'wake' };
  }

  if (msg['type'] === 'move_item') {
    /* ⚠️ Shape only, again — whether the slots are in range and whether the
       move is *allowed* both belong to the inventory rules, which is the one
       place that knows what is in the pack. Integers rather than merely finite:
       half a slot is not a slot and half an item is not an item. */
    const { from, to, count } = msg;
    if (!Number.isInteger(from) || !Number.isInteger(to) || !Number.isInteger(count)) return null;
    return {
      type: 'move_item',
      from: from as number,
      to: to as number,
      count: count as number,
    };
  }

  if (msg['type'] === 'move_hearth_item') {
    /* ⚠️ Shape only, as everywhere in this function. Whether that block really
       is a hearth, whether the player can reach it, whether the slot takes that
       item and whether they own it are all the SERVER's questions — this only
       guarantees nothing further down has to wonder what it was handed.
       🔴 `where` is checked against the two literal strings rather than merely
       being a string: an unrecognised container would otherwise reach code that
       has exactly two branches and quietly take the wrong one. */
    const { at, from, to, count } = msg;
    if (!Number.isInteger(count)) return null;
    const place = (v: unknown): { where: 'hearth' | 'player'; slot: number } | null => {
      if (typeof v !== 'object' || v === null) return null;
      const { where, slot } = v as Record<string, unknown>;
      if (where !== 'hearth' && where !== 'player') return null;
      if (!Number.isInteger(slot)) return null;
      return { where, slot: slot as number };
    };
    if (typeof at !== 'object' || at === null) return null;
    const { x, y, z } = at as Record<string, unknown>;
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) return null;
    const f = place(from);
    const t = place(to);
    if (f === null || t === null) return null;
    return {
      type: 'move_hearth_item',
      at: { x: x as number, y: y as number, z: z as number },
      from: f,
      to: t,
      count: count as number,
    };
  }

  if (msg['type'] === 'move_chest_item') {
    /* ⚠️ Shape only, as everywhere in this function — and deliberately the
       SAME shape check as the hearth's, with its own two literal strings.
       🔴 Sharing one `place` helper between them would have to accept all four
       words, and a 'hearth' end reaching the chest handler is exactly the
       "right item, wrong box" failure both messages name their containers to
       prevent. Two small duplicated checks beat one that is too permissive. */
    const { at, from, to, count } = msg;
    if (!Number.isInteger(count)) return null;
    const place = (v: unknown): { where: 'chest' | 'player'; slot: number } | null => {
      if (typeof v !== 'object' || v === null) return null;
      const { where, slot } = v as Record<string, unknown>;
      if (where !== 'chest' && where !== 'player') return null;
      if (!Number.isInteger(slot)) return null;
      return { where, slot: slot as number };
    };
    if (typeof at !== 'object' || at === null) return null;
    const { x, y, z } = at as Record<string, unknown>;
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) return null;
    const f = place(from);
    const t = place(to);
    if (f === null || t === null) return null;
    return {
      type: 'move_chest_item',
      at: { x: x as number, y: y as number, z: z as number },
      from: f,
      to: t,
      count: count as number,
    };
  }

  if (msg['type'] === 'ask_nearby_materials') {
    /**
     * 🔴 **NOTHING TO CHECK, AND THAT IS THE POINT** — T172, protocol 25.
     *
     * The message carries no fields: the server answers from `session.at`, the
     * position `craft` is judged from. ⚠️ **A message with no payload still
     * has to be listed here.** This function is an ALLOWLIST, and anything it
     * does not name is `null` — which makes the server `socket.close(1003)`.
     *
     * 🔴 **That is exactly what shipped on 2026-09-07 and haugur found in
     * minutes:** the craft screen sent this, the server called it malformed and
     * **hung up on him**, so the next thing he pressed — `O` on a skemma — did
     * nothing at all. 🎯 *"skemma and býtbúr dont open up when i press o."*
     * **The fault looked like a container bug and was a disconnect.**
     */
    return { type: 'ask_nearby_materials' };
  }

  if (msg['type'] === 'move_bur_item') {
    /* 🥫🔥 T222c, protocol 34. Shape only: that the block is a búr, that it is
       in reach, and both ends' own rules are the SERVER's questions, asked
       against its own world. */
    const at = msg['at'];
    if (typeof at !== 'object' || at === null) return null;
    const p = at as Record<string, unknown>;
    if (typeof p['x'] !== 'number' || typeof p['y'] !== 'number' || typeof p['z'] !== 'number') {
      return null;
    }
    if (!Number.isFinite(p['x']) || !Number.isFinite(p['y']) || !Number.isFinite(p['z'])) {
      return null;
    }
    const end = (v: unknown): { where: 'store' | 'fire'; slot: number } | null => {
      if (typeof v !== 'object' || v === null) return null;
      const e = v as Record<string, unknown>;
      const where = e['where'];
      const slot = e['slot'];
      if (where !== 'store' && where !== 'fire') return null;
      if (typeof slot !== 'number' || !Number.isInteger(slot) || slot < 0) return null;
      return { where, slot };
    };
    const from = end(msg['from']);
    const to = end(msg['to']);
    const count = msg['count'];
    if (from === null || to === null) return null;
    if (typeof count !== 'number' || !Number.isInteger(count) || count <= 0) return null;
    return {
      type: 'move_bur_item',
      at: { x: p['x'], y: p['y'], z: p['z'] },
      from,
      to,
      count,
    };
  }

  if (msg['type'] === 'ask_hearth_contents') {
    /* 🍲 T222, protocol 32. Shape only: that the block there is a hearth and
       that it is within reach are the SERVER's questions, asked against its own
       world and its own idea of where this player is. */
    const at = msg['at'];
    if (typeof at !== 'object' || at === null) return null;
    const p = at as Record<string, unknown>;
    if (typeof p['x'] !== 'number' || typeof p['y'] !== 'number' || typeof p['z'] !== 'number') {
      return null;
    }
    if (!Number.isFinite(p['x']) || !Number.isFinite(p['y']) || !Number.isFinite(p['z'])) {
      return null;
    }
    return { type: 'ask_hearth_contents', at: { x: p['x'], y: p['y'], z: p['z'] } };
  }

  if (msg['type'] === 'craft') {
    /* ⚠️ Shape only. Whether the recipe EXISTS, whether it is reachable,
       whether that really is a bench and whether you have the materials are all
       questions for the server's recipe and world code — the same division
       every other message here keeps. */
    const { recipe, at } = msg;
    if (typeof recipe !== 'string' || recipe.length === 0 || recipe.length > 64) return null;
    if (at === null || at === undefined) return { type: 'craft', recipe, at: null };
    if (typeof at !== 'object') return null;
    const { x, y, z } = at as Record<string, unknown>;
    /* Integers, not merely finite: half a block is not a block. */
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) return null;
    return { type: 'craft', recipe, at: { x: x as number, y: y as number, z: z as number } };
  }

  return null;
}
